// Vendas.js - Atualizado 2026-01-18 - Modal completo
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import WhatsAppIcon from '../components/WhatsAppIcon';
import EditarVendaModal from '../components/EditarVendaModal';
import { MeiosEnvioList } from '../components/MeioEnvioLabel';
import { salesService, usersService, operadorasService } from '../services/api';
import { formatDate, formatDateOnly, formatDateTime, maskCPF, maskCNPJ, maskPhone, maskCEP, maskMoney, unmaskMoney, parseDateDDMMYYYY, maskDateDDMMYYYY } from '../utils/masks';
import { parseObservacoesTratamento } from '../utils/observacoesTratamento';
import {
  getCooldownRestante,
  usuarioSemLimiteCooldown,
  formatCountdown,
  mensagemCooldown,
  LIMITE_COOLDOWN_MINUTOS
} from '../utils/cooldownVendedor';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Vendas.css';

import { formatMarcaLabel } from '../utils/marcas';

const formatMarca = (marca) => formatMarcaLabel(marca);

// Data de hoje em Brasília no formato YYYY-MM-DD (para data_instalacao)
const getTodayBrasilia = () => {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
};

// Mês/ano atuais em Brasília (America/Sao_Paulo)
const getCurrentMonthYearBrasilia = () => {
  const now = new Date();
  const brtMs = now.getTime() - 3 * 60 * 60 * 1000;
  const d = new Date(brtMs);
  return { mes: d.getUTCMonth() + 1, ano: d.getUTCFullYear() };
};

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function useCooldownCampo(ultimaDevolucao, user) {
  const [tempoRestante, setTempoRestante] = React.useState(null);

  React.useEffect(() => {
    const tick = () => {
      if (usuarioSemLimiteCooldown(user)) {
        setTempoRestante(null);
        return;
      }
      const info = getCooldownRestante(ultimaDevolucao);
      if (info.podeAgir) {
        setTempoRestante(null);
      } else {
        setTempoRestante({
          minutos: info.minutosRestantes,
          segundos: info.segundosRestantes
        });
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [ultimaDevolucao, user?.role]);

  return tempoRestante;
}

function validarCooldownOuAlertar(user, ultimaDevolucao) {
  if (usuarioSemLimiteCooldown(user)) return true;
  const msg = mensagemCooldown(getCooldownRestante(ultimaDevolucao));
  if (msg) {
    alert(msg);
    return false;
  }
  return true;
}

// Componente de botão de verificar aceite com countdown
const VerificarAceiteButton = ({ venda, user, onVerificar, verificandoAceite }) => {
  const tempoRestante = useCooldownCampo(venda.ultima_solicitacao_verificacao, user);
  const isAdminOrDiretor = usuarioSemLimiteCooldown(user);
  const isVendedor = user?.role === 'vendedor' || user?.role === 'supervisor';
  
  if (!isVendedor && !isAdminOrDiretor) {
    return null;
  }
  
  const podeSolicitar = tempoRestante === null;
  
  return (
    <div style={{ marginTop: '1rem' }}>
      <button
        onClick={() => onVerificar(venda)}
        disabled={!podeSolicitar || verificandoAceite}
        style={{
          padding: '0.75rem 1.5rem',
          background: podeSolicitar ? 'var(--azul-dvs)' : '#666',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: podeSolicitar ? 'pointer' : 'not-allowed',
          fontSize: '1rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          opacity: podeSolicitar ? 1 : 0.7
        }}
      >
        {verificandoAceite ? (
          <>⏳ Enviando solicitação...</>
        ) : tempoRestante ? (
          <>⏱️ Aguarde {formatCountdown(tempoRestante)} min</>
        ) : (
          <>✅ Verificar Aceite</>
        )}
      </button>
      {!isAdminOrDiretor && (
        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
          {tempoRestante 
            ? `Você pode solicitar uma nova verificação a cada ${LIMITE_COOLDOWN_MINUTOS} minutos.`
            : 'Ao clicar, a venda voltará para a fila de tratamento do BKO.'
          }
        </p>
      )}
      {isAdminOrDiretor && (
        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
          Como administrador, você pode solicitar verificação a qualquer momento.
        </p>
      )}
    </div>
  );
};

// Componente de botão de verificar biometria com countdown (mesmo limite de 20 min)
const VerificarBiometriaButton = ({ venda, user, onVerificar, verificandoBiometria }) => {
  const tempoRestante = useCooldownCampo(venda.ultima_devolucao_biometria, user);

  const isAdminOrDiretor = usuarioSemLimiteCooldown(user);
  const isVendedor = user?.role === 'vendedor' || user?.role === 'supervisor';

  if (!isVendedor && !isAdminOrDiretor) {
    return null;
  }

  const podeSolicitar = tempoRestante === null;

  return (
    <div style={{ marginTop: '1rem' }}>
      <button
        onClick={() => onVerificar(venda)}
        disabled={!podeSolicitar || verificandoBiometria}
        style={{
          padding: '0.75rem 1.5rem',
          background: podeSolicitar ? 'var(--azul-dvs)' : '#666',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: podeSolicitar ? 'pointer' : 'not-allowed',
          fontSize: '1rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          opacity: podeSolicitar ? 1 : 0.7
        }}
      >
        {verificandoBiometria ? (
          <>⏳ Enviando solicitação...</>
        ) : tempoRestante ? (
          <>⏱️ Aguarde {formatCountdown(tempoRestante)} min</>
        ) : (
          <>✅ Verificar Biometria</>
        )}
      </button>
      {!isAdminOrDiretor && (
        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
          {tempoRestante
            ? `Você pode solicitar uma nova verificação a cada ${LIMITE_COOLDOWN_MINUTOS} minutos.`
            : 'Ao clicar, a venda voltará para a fila de tratamento do BKO.'}
        </p>
      )}
      {isAdminOrDiretor && (
        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
          Como administrador, você pode solicitar verificação a qualquer momento.
        </p>
      )}
    </div>
  );
};

const ClienteRespondeuWhatsappButton = ({ venda, user, onConfirmar, processando }) => {
  const tempoRestante = useCooldownCampo(venda.ultima_devolucao_whatsapp, user);
  const podeAgir = tempoRestante === null;

  return (
    <button
      type="button"
      onClick={() => onConfirmar(venda)}
      className="btn-small"
      disabled={processando || !podeAgir}
      style={{
        background: podeAgir ? '#128c7e' : '#666',
        color: '#fff',
        cursor: podeAgir && !processando ? 'pointer' : 'not-allowed',
        opacity: podeAgir ? 1 : 0.75
      }}
      title={
        tempoRestante
          ? `Aguarde ${formatCountdown(tempoRestante)} para devolver novamente`
          : 'Informa ao BKO que o cliente já respondeu no WhatsApp; a venda volta para Pendente BKO com registro no histórico.'
      }
    >
      {processando
        ? '...'
        : tempoRestante
          ? `Aguarde ${formatCountdown(tempoRestante)}`
          : 'Cliente já respondeu'}
    </button>
  );
};

const BotoesAlterarDadosAceite = ({
  venda,
  user,
  onAlterarEmail,
  onAlterarCelular,
  salvandoEmail,
  salvandoCelular
}) => {
  const tempoRestante = useCooldownCampo(venda.ultima_solicitacao_verificacao, user);
  const podeAgir = tempoRestante === null;
  const bloqueado = !podeAgir || salvandoEmail || salvandoCelular;

  return (
    <>
      <div style={{ marginTop: '1rem' }}>
        <button
          type="button"
          onClick={() => onAlterarEmail(venda)}
          disabled={bloqueado}
          style={{
            padding: '0.75rem 1.5rem',
            background: podeAgir ? '#5c6bc0' : '#666',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: bloqueado ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            opacity: bloqueado ? 0.7 : 1
          }}
        >
          {tempoRestante ? `✉️ Aguarde ${formatCountdown(tempoRestante)}` : '✉️ Alterar email'}
        </button>
        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem', maxWidth: '22rem' }}>
          {tempoRestante
            ? `Aguarde ${LIMITE_COOLDOWN_MINUTOS} minutos entre devoluções ao BKO.`
            : 'Atualiza o email do cliente e devolve a venda para a fila do BKO com registro no histórico de tratamento.'}
        </p>
      </div>
      <div style={{ marginTop: '1rem' }}>
        <button
          type="button"
          onClick={() => onAlterarCelular(venda)}
          disabled={bloqueado}
          style={{
            padding: '0.75rem 1.5rem',
            background: podeAgir ? '#5c6bc0' : '#666',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: bloqueado ? 'not-allowed' : 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            opacity: bloqueado ? 0.7 : 1
          }}
        >
          {tempoRestante ? `📱 Aguarde ${formatCountdown(tempoRestante)}` : '📱 Alterar celular'}
        </button>
        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem', maxWidth: '22rem' }}>
          {tempoRestante
            ? `Aguarde ${LIMITE_COOLDOWN_MINUTOS} minutos entre devoluções ao BKO.`
            : 'Atualiza o celular do cliente e devolve a venda para a fila do BKO com registro no histórico de tratamento.'}
        </p>
      </div>
    </>
  );
};

const STATUS_OPTIONS = [
  { value: 'pendente_bko', label: 'Pendente BKO' },
  { value: 'tratando', label: 'Tratando' },
  { value: 'pendente_vendedor', label: 'Pendente Vendedor' },
  { value: 'pendente_sistemica', label: 'Pendente Sistêmica' },
  { value: 'pendente_biometria', label: 'Pendente Biometria' },
  { value: 'pendente_auditoria', label: 'Pendente Auditoria' },
  { value: 'pendente_imput', label: 'Pendente Imput' },
  { value: 'tramitacao_manual', label: 'Tramitação Manual' },
  { value: 'critica', label: 'Crítica' },
  { value: 'mesa_de_credito', label: 'Mesa de Crédito' },
  { value: 'aguardando_whatsapp', label: 'Aguardando WhatsApp' },
  { value: 'aguardando_dados_sfa', label: 'Aguardando dados SFA' },
  { value: 'aguardando_instalacao', label: 'Aguardando Instalação' },
  { value: 'validacao_ok', label: 'Validação OK' },
  { value: 'aguardando_aceite', label: 'Aguardando Aceite' },
  { value: 'instalacao_cancelada', label: 'Instalação cancelada' },
  { value: 'concluido', label: 'Concluído' },
  { value: 'cancelado', label: 'Cancelado' },
  { value: 'desistencia', label: 'Desistencia' },
  { value: 'estorno', label: 'Estorno' },
  { value: 'sem_viabilidade', label: 'Sem Viabilidade' },
  { value: 'aguardando_estruturante', label: 'Aguardando Estruturante' },
  { value: 'aguardando_can_ou_reag', label: 'Aguardando CAN ou REAG' },
  { value: 'cancelamento_comercial', label: 'Cancelamento comercial' },
  { value: 'cancelamento_tecnico', label: 'Cancelamento técnico' }
];

const Vendas = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminOrDiretor = user?.role === 'admin' || user?.role === 'diretor';
  const isSupervisor = user?.role === 'supervisor';
  const [vendas, setVendas] = useState([]);
  const [filteredVendas, setFilteredVendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [editingVenda, setEditingVenda] = useState(null);
  const [changingStatus, setChangingStatus] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newDataInstalacao, setNewDataInstalacao] = useState('');
  const [newNumeroOrdem, setNewNumeroOrdem] = useState('');
  const [newDataConclusao, setNewDataConclusao] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [supervisores, setSupervisores] = useState([]);
  const [changingAtribuicao, setChangingAtribuicao] = useState(null);
  const [novoTratadoPor, setNovoTratadoPor] = useState('');
  const [backoffices, setBackoffices] = useState([]);
  const [changingVendedor, setChangingVendedor] = useState(null);
  const [novoVendedorId, setNovoVendedorId] = useState('');
  const [novaObservacaoSupervisao, setNovaObservacaoSupervisao] = useState('');
  const [addingObservacaoSupervisao, setAddingObservacaoSupervisao] = useState(false);
  const [verificandoAceite, setVerificandoAceite] = useState(false);
  const [verificandoBiometria, setVerificandoBiometria] = useState(false);
  const [clienteRespondeuWhatsappId, setClienteRespondeuWhatsappId] = useState(null);
  const [clienteAtualizouSfaId, setClienteAtualizouSfaId] = useState(null);
  const [vendedorCancelouOuReagendouId, setVendedorCancelouOuReagendouId] = useState(null);
  const [tempoRestanteVerificacao, setTempoRestanteVerificacao] = useState({});
  const [reenviandoBiometria, setReenviandoBiometria] = useState(null);
  const [metodoReenvio, setMetodoReenvio] = useState('');
  const [alterarEmailVenda, setAlterarEmailVenda] = useState(null);
  const [novoEmailAlterarAceite, setNovoEmailAlterarAceite] = useState('');
  const [salvandoEmailAceite, setSalvandoEmailAceite] = useState(false);
  const [alterarCelularVenda, setAlterarCelularVenda] = useState(null);
  const [novoCelularAlterarAceite, setNovoCelularAlterarAceite] = useState('');
  const [salvandoCelularAceite, setSalvandoCelularAceite] = useState(false);

  // Processar Vendas (admin/diretor)
  const [showProcessarVendas, setShowProcessarVendas] = useState(false);
  const [processarStep, setProcessarStep] = useState('tipo');
  const [processarTipo, setProcessarTipo] = useState('');
  const [processarAcao, setProcessarAcao] = useState('');
  const [processarLoading, setProcessarLoading] = useState(false);
  const [processarResultado, setProcessarResultado] = useState(null);

  // Processar Planilha ATT ALLIANCE (admin/diretor)
  const [showProcessarPlanilha, setShowProcessarPlanilha] = useState(false);
  const [processarPlanilhaLoading, setProcessarPlanilhaLoading] = useState(false);
  const processarPlanilhaFileInputRef = useRef(null);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPeriodoTipo, setExportPeriodoTipo] = useState('mes');
  const [exportMes, setExportMes] = useState(() => getCurrentMonthYearBrasilia().mes);
  const [exportAno, setExportAno] = useState(() => getCurrentMonthYearBrasilia().ano);
  const [exportDataInicioDisplay, setExportDataInicioDisplay] = useState('');
  const [exportDataFimDisplay, setExportDataFimDisplay] = useState('');
  const [exportDataInicio, setExportDataInicio] = useState('');
  const [exportDataFim, setExportDataFim] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const processarFileInputRef = useRef(null);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Estatísticas
  const [stats, setStats] = useState({
    total: 0,
    pendentesBko: 0,
    pendentesVendedor: 0,
    pendentesSistemica: 0,
    pendentesBiometria: 0,
    pendentesAuditoria: 0,
    pendentesImput: 0,
    aguardandoWhatsapp: 0,
    aguardandoDadosSfa: 0,
    aguardandoInstalacao: 0,
    concluidas: 0,
    canceladas: 0,
    desistencia: 0,
    cancelamentoComercial: 0,
    cancelamentoTecnico: 0,
    estorno: 0
  });
  
  const currentMonthBrasilia = useMemo(getCurrentMonthYearBrasilia, []);
  // Filtros: por padrão "mês atual" (Brasília); mes/ano vazios = "Todos os meses"
  const [filtros, setFiltros] = useState({
    status_mode: '',
    status_list: [],
    busca: '',
    vendedor_id: '',
    supervisor_id: '',
    operadora_id: '',
    tipo_cliente: '',
    marca: '',
    data_inicio: '',
    data_fim: '',
    data_instalacao_inicio: '',
    data_instalacao_fim: '',
    mes: currentMonthBrasilia.mes,
    ano: currentMonthBrasilia.ano
  });
  const [operadoras, setOperadoras] = useState([]);
  // Exibição dos filtros de data (dd/mm/yyyy) — evita bug de 1 dia a menos no celular com type="date"
  const [dataInicioDisplay, setDataInicioDisplay] = useState('');
  const [dataFimDisplay, setDataFimDisplay] = useState('');
  const [dataInstalacaoInicioDisplay, setDataInstalacaoInicioDisplay] = useState('');
  const [dataInstalacaoFimDisplay, setDataInstalacaoFimDisplay] = useState('');

  useEffect(() => {
    loadVendedores();
    loadOperadoras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role]);

  useEffect(() => {
    loadVendas();
    loadStats(); // Recarregar estatísticas também quando filtros mudarem
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, filtros.status_mode, filtros.status_list, filtros.vendedor_id, filtros.supervisor_id, filtros.operadora_id, filtros.tipo_cliente, filtros.marca, filtros.data_inicio, filtros.data_fim, filtros.data_instalacao_inicio, filtros.data_instalacao_fim, filtros.mes, filtros.ano, filtros.busca]);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendas, filtros.busca]);

  const loadVendedores = async () => {
    try {
      if (user?.role === 'supervisor' && user?.id) {
        const subordinados = await usersService.getVendedoresBySupervisor(user.id);
        const eu = { id: user.id, nome: user.nome || user.username, username: user.username };
        setVendedores([eu, ...(subordinados || [])]);
        setSupervisores([]);
        setBackoffices([]);
        return;
      }

      if (!['admin', 'diretor'].includes(user?.role)) {
        setVendedores([]);
        setSupervisores([]);
        return;
      }

      const users = await usersService.getAll();
      const list = Array.isArray(users) ? users : (users?.data || []);
      // Vendedores + supervisores (podem ter vendas próprias) + admin/diretor se necessário
      const vendedoresList = list.filter(u =>
        u.role === 'vendedor' || u.role === 'supervisor' || u.role === 'admin' || u.role === 'diretor'
      );
      setVendedores(vendedoresList);
      setSupervisores(list.filter(u => u.role === 'supervisor'));

      const backofficesList = list.filter(u => u.role === 'backoffice' && u.ativo);
      setBackoffices(backofficesList);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
    }
  };

  const loadOperadoras = async () => {
    try {
      const data = await operadorasService.getAll();
      setOperadoras(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar operadoras:', error);
    }
  };

  const loadVendas = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtros.status_mode === 'include' && filtros.status_list && filtros.status_list.length > 0) {
        filters.status = filtros.status_list.join(',');
      } else if (filtros.status_mode === 'exclude' && filtros.status_list && filtros.status_list.length > 0) {
        filters.status_excluir = filtros.status_list.join(',');
      }
      if (filtros.vendedor_id && filtros.vendedor_id.trim() !== '' && ['admin', 'diretor', 'supervisor'].includes(user?.role)) {
        filters.vendedor_id = filtros.vendedor_id;
      }
      if (filtros.supervisor_id && filtros.supervisor_id.trim() !== '' && ['admin', 'diretor'].includes(user?.role)) {
        filters.supervisor_id = filtros.supervisor_id;
      }
      if (filtros.operadora_id && filtros.operadora_id.trim() !== '') {
        filters.operadora_id = filtros.operadora_id;
      }
      if (filtros.tipo_cliente && filtros.tipo_cliente.trim() !== '') {
        filters.tipo_cliente = filtros.tipo_cliente;
      }
      if (filtros.marca && filtros.marca.trim() !== '') {
        filters.marca = filtros.marca;
      }
      // Intervalo de datas explícito tem prioridade; senão usar mes/ano (vendas do mês em Brasília)
      if (filtros.data_inicio && filtros.data_fim && filtros.data_inicio.trim() !== '' && filtros.data_fim.trim() !== '') {
        filters.data_inicio = filtros.data_inicio;
        filters.data_fim = filtros.data_fim;
      } else if (filtros.mes && filtros.ano && filtros.mes >= 1 && filtros.mes <= 12 && filtros.ano >= 2000) {
        filters.mes = filtros.mes;
        filters.ano = filtros.ano;
      }
      if (filtros.data_instalacao_inicio && filtros.data_instalacao_fim && filtros.data_instalacao_inicio.trim() !== '' && filtros.data_instalacao_fim.trim() !== '') {
        filters.data_instalacao_inicio = filtros.data_instalacao_inicio;
        filters.data_instalacao_fim = filtros.data_instalacao_fim;
      }
      if (filtros.busca && filtros.busca.trim() !== '') {
        filters.busca = filtros.busca.trim();
      }

      const response = await salesService.getAll(filters, currentPage, itemsPerPage);
      
      if (response.data && response.pagination) {
        setVendas(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotal(response.pagination.total);
      } else {
        // Fallback para compatibilidade com resposta antiga
        setVendas(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
      alert('Erro ao carregar vendas');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const filters = {};
      if (filtros.vendedor_id && ['admin', 'diretor', 'supervisor'].includes(user?.role)) {
        filters.vendedor_id = filtros.vendedor_id;
      }
      if (filtros.supervisor_id && ['admin', 'diretor'].includes(user?.role)) {
        filters.supervisor_id = filtros.supervisor_id;
      }
      if (filtros.operadora_id && filtros.operadora_id.trim() !== '') {
        filters.operadora_id = filtros.operadora_id;
      }
      if (filtros.tipo_cliente && filtros.tipo_cliente.trim() !== '') {
        filters.tipo_cliente = filtros.tipo_cliente;
      }
      if (filtros.marca && filtros.marca.trim() !== '') {
        filters.marca = filtros.marca;
      }
      if (filtros.data_inicio && filtros.data_fim && filtros.data_inicio.trim() !== '' && filtros.data_fim.trim() !== '') {
        filters.data_inicio = filtros.data_inicio;
        filters.data_fim = filtros.data_fim;
      } else if (filtros.mes && filtros.ano && filtros.mes >= 1 && filtros.mes <= 12 && filtros.ano >= 2000) {
        filters.mes = filtros.mes;
        filters.ano = filtros.ano;
      }
      if (filtros.data_instalacao_inicio && filtros.data_instalacao_fim && filtros.data_instalacao_inicio.trim() !== '' && filtros.data_instalacao_fim.trim() !== '') {
        filters.data_instalacao_inicio = filtros.data_instalacao_inicio;
        filters.data_instalacao_fim = filtros.data_instalacao_fim;
      }
      
      const response = await salesService.getStatsCounts(filters);
      
      setStats({
        total: response.total || 0,
        pendentesBko: response.pendentesBko || 0,
        pendentesVendedor: response.pendentesVendedor || 0,
        pendentesSistemica: response.pendentesSistemica || 0,
        pendentesBiometria: response.pendentesBiometria || 0,
        pendentesAuditoria: response.pendentesAuditoria || 0,
        pendentesImput: response.pendentesImput || 0,
        aguardandoWhatsapp: response.aguardandoWhatsapp || 0,
        aguardandoDadosSfa: response.aguardandoDadosSfa || 0,
        aguardandoCanOuReag: response.aguardandoCanOuReag || 0,
        aguardandoInstalacao: response.aguardandoInstalacao || 0,
        concluidas: response.concluidas || 0,
        canceladas: response.canceladas || 0,
        desistencia: response.desistencia || 0,
        cancelamentoComercial: response.cancelamentoComercial || 0,
        cancelamentoTecnico: response.cancelamentoTecnico || 0,
        estorno: response.estorno || 0
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const applyFilters = () => {
    // Busca (CPF/CNPJ/Nome/número da ordem/código PAP) é feita no backend; aceita com ou sem ponto e traço. Resultados já vêm filtrados em vendas.
    setFilteredVendas([...vendas]);
  };

  const handleFilterChange = (name, value) => {
    if (['status_mode', 'status_list', 'vendedor_id', 'supervisor_id', 'operadora_id', 'tipo_cliente', 'marca', 'data_inicio', 'data_fim', 'data_instalacao_inicio', 'data_instalacao_fim', 'mes', 'ano', 'busca'].includes(name)) {
      setCurrentPage(1);
    }
    setFiltros(prev => {
      const newFiltros = { ...prev, [name]: value };
      if (name === 'tipo_cliente' && value === 'cnpj') {
        newFiltros.marca = '';
      }
      if (name === 'data_inicio' || name === 'data_fim') {
        newFiltros.mes = '';
        newFiltros.ano = '';
      }
      if (name === 'mes' || name === 'ano') {
        newFiltros.data_inicio = '';
        newFiltros.data_fim = '';
      }
      return newFiltros;
    });
  };

  const handleStatusListToggle = (statusValue) => {
    setCurrentPage(1);
    setFiltros(prev => {
      const list = prev.status_list || [];
      const idx = list.indexOf(statusValue);
      const newList = idx >= 0 ? list.filter(s => s !== statusValue) : [...list, statusValue];
      return { ...prev, status_list: newList };
    });
  };

  const handleDataFiltroChange = (field, rawValue) => {
    setCurrentPage(1);
    const masked = maskDateDDMMYYYY(rawValue);
    if (field === 'data_inicio') {
      setDataInicioDisplay(masked);
    } else if (field === 'data_fim') {
      setDataFimDisplay(masked);
    } else if (field === 'data_instalacao_inicio') {
      setDataInstalacaoInicioDisplay(masked);
    } else {
      setDataInstalacaoFimDisplay(masked);
    }
    const iso = parseDateDDMMYYYY(masked);
    setFiltros(prev => ({ ...prev, [field]: iso || '', ...(field.startsWith('data_instalacao') ? {} : { mes: '', ano: '' }) }));
  };

  const handleApplyFilters = () => {
    applyFilters();
  };

  const handleClearFilters = () => {
    setCurrentPage(1);
    const current = getCurrentMonthYearBrasilia();
    setFiltros({
      status_mode: '',
      status_list: [],
      busca: '',
      vendedor_id: '',
      supervisor_id: '',
      operadora_id: '',
      tipo_cliente: '',
      marca: '',
      data_inicio: '',
      data_fim: '',
      data_instalacao_inicio: '',
      data_instalacao_fim: '',
      mes: current.mes,
      ano: current.ano
    });
    setDataInicioDisplay('');
    setDataFimDisplay('');
    setDataInstalacaoInicioDisplay('');
    setDataInstalacaoFimDisplay('');
  };

  // Opções de mês para o select (últimos 24 meses + Todos os meses)
  const opcoesMesesVendas = useMemo(() => {
    const opts = [{ value: '', label: 'Todos os meses' }];
    const { mes: m0, ano: a0 } = getCurrentMonthYearBrasilia();
    for (let i = 0; i < 24; i++) {
      let mes = m0 - i;
      let ano = a0;
      while (mes <= 0) { mes += 12; ano -= 1; }
      opts.push({ value: `${mes}|${ano}`, label: `${MESES[mes - 1]} ${ano}` });
    }
    return opts;
  }, []);

  const handleMonthFilterChange = (value) => {
    setCurrentPage(1);
    if (!value) {
      setFiltros(prev => ({ ...prev, mes: '', ano: '', data_inicio: '', data_fim: '' }));
      setDataInicioDisplay('');
      setDataFimDisplay('');
    } else {
      const [m, a] = value.split('|').map(Number);
      setFiltros(prev => ({ ...prev, mes: m, ano: a, data_inicio: '', data_fim: '' }));
      setDataInicioDisplay('');
      setDataFimDisplay('');
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Resetar para primeira página ao mudar itens por página
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'pendente_bko': 'status-pendente-bko',
      'tratando': 'status-tratando',
      'pendente_vendedor': 'status-pendente-vendedor',
      'pendente_sistemica': 'status-pendente-sistemica',
      'pendente_biometria': 'status-pendente-biometria',
      'pendente_auditoria': 'status-pendente-auditoria',
      'pendente_imput': 'status-pendente-imput',
      'tramitacao_manual': 'status-tramitacao-manual',
      'critica': 'status-critica',
      'mesa_de_credito': 'status-mesa-de-credito',
      'aguardando_whatsapp': 'status-aguardando-whatsapp',
      'aguardando_dados_sfa': 'status-aguardando-dados-sfa',
      'aguardando_instalacao': 'status-aguardando-instalacao',
      'validacao_ok': 'status-validacao-ok',
      'instalacao_cancelada': 'status-instalacao-cancelada',
      'concluido': 'status-concluido',
      'cancelado': 'status-cancelado',
      'desistencia': 'status-desistencia',
      'estorno': 'status-estorno',
      'aguardando_aceite': 'status-aguardando-aceite',
      'sem_viabilidade': 'status-sem-viabilidade',
      'aguardando_estruturante': 'status-aguardando-estruturante',
      'aguardando_can_ou_reag': 'status-aguardando-can-ou-reag',
      'cancelamento_comercial': 'status-cancelamento-comercial',
      'cancelamento_tecnico': 'status-cancelamento-tecnico'
    };
    return statusMap[status] || 'status-pendente-bko';
  };

  const getStatusText = (status) => {
    const texts = {
      'pendente_bko': 'Pendente BKO',
      'tratando': 'Tratando',
      'pendente_vendedor': 'Pendente Vendedor',
      'pendente_sistemica': 'Pendente Sistêmica',
      'pendente_biometria': 'Pendente Biometria',
      'pendente_auditoria': 'Pendente Auditoria',
      'pendente_imput': 'Pendente Imput',
      'tramitacao_manual': 'Tramitação Manual',
      'critica': 'Crítica',
      'mesa_de_credito': 'Mesa de Crédito',
      'aguardando_whatsapp': 'Aguardando WhatsApp',
      'aguardando_dados_sfa': 'Aguardando dados SFA',
      'aguardando_instalacao': 'Aguardando Instalação',
      'validacao_ok': 'Validação OK',
      'instalacao_cancelada': 'Instalação cancelada',
      'concluido': 'Concluído',
      'cancelado': 'Cancelado',
      'desistencia': 'Desistencia',
      'estorno': 'Estorno',
      'aguardando_aceite': 'Aguardando Aceite',
      'sem_viabilidade': 'Sem Viabilidade',
      'aguardando_estruturante': 'Aguardando Estruturante',
      'aguardando_can_ou_reag': 'Aguardando CAN ou REAG',
      'cancelamento_comercial': 'Cancelamento comercial',
      'cancelamento_tecnico': 'Cancelamento técnico'
    };
    return texts[status] || status;
  };

  const isValidacaoOkRobo = (venda) =>
    (venda?.status === 'validacao_ok' || venda?.status === 'aguardando_instalacao')
    && !venda?.data_instalacao
    && !venda?.numero_ordem;

  const formatDocumento = (doc) => {
    if (!doc) return '';
    const clean = doc.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (clean.length === 14) {
      return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  const parseCnpjsDisponiveis = (venda) => {
    if (!venda?.cnpjs_disponiveis) return [];
    try {
      const parsed = typeof venda.cnpjs_disponiveis === 'string'
        ? JSON.parse(venda.cnpjs_disponiveis)
        : venda.cnpjs_disponiveis;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    const clean = phone.replace(/\D/g, '');
    if (clean.length === 11) {
      return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (clean.length === 10) {
      return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const parseObservacoesSupervisao = (texto) => {
    if (!texto || !texto.trim()) return [];
    const linhas = texto.trim().split('\n').filter(l => l.trim());
    const obs = [];
    linhas.forEach(linha => {
      const m = linha.trim().match(/^\[([^\]]+)\]\s*(.+?):\s*(.+)$/);
      if (m) obs.push({ dataHora: m[1], usuario: m[2], texto: m[3] });
      else if (linha.trim()) obs.push({ tipo: 'texto', texto: linha.trim() });
    });
    return obs.reverse();
  };

  const handleAddObservacaoSupervisao = async () => {
    if (!selectedVenda || !novaObservacaoSupervisao.trim()) return;
    setAddingObservacaoSupervisao(true);
    try {
      const { sale } = await salesService.addObservacaoSupervisao(selectedVenda.id, novaObservacaoSupervisao.trim());
      setSelectedVenda(sale);
      setNovaObservacaoSupervisao('');
    } catch (error) {
      console.error('Erro ao adicionar observação:', error);
      alert('Erro ao adicionar observação: ' + (error.response?.data?.error || error.message));
    } finally {
      setAddingObservacaoSupervisao(false);
    }
  };

  const viewDetails = async (id) => {
    try {
      const venda = await salesService.getById(id);
      setSelectedVenda(venda);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      alert('Erro ao carregar detalhes da venda');
    }
  };

  const closeModal = () => {
    setSelectedVenda(null);
    setEditingVenda(null);
    setChangingStatus(null);
    setNewStatus('');
    setNewDataInstalacao('');
    setNewNumeroOrdem('');
    setNewDataConclusao('');
    setChangingAtribuicao(null);
    setNovoTratadoPor('');
    setChangingVendedor(null);
    setNovoVendedorId('');
    setNovaObservacaoSupervisao('');
    setReenviandoBiometria(null);
    setMetodoReenvio('');
    setAlterarEmailVenda(null);
    setNovoEmailAlterarAceite('');
    setAlterarCelularVenda(null);
    setNovoCelularAlterarAceite('');
  };
  
  const openAtribuicaoBko = (venda) => {
    setChangingAtribuicao(venda);
    setNovoTratadoPor(venda.tratado_por || '');
  };

  const openAtribuicaoVendedor = (venda) => {
    setChangingVendedor(venda);
    setNovoVendedorId(venda.vendedor_id || '');
  };

  const handleAtribuicaoVendedorSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!novoVendedorId || novoVendedorId.toString().trim() === '') {
        alert('❌ Por favor, selecione um vendedor');
        return;
      }

      const vendedorId = parseInt(novoVendedorId);
      if (isNaN(vendedorId) || vendedorId <= 0) {
        alert('❌ ID de vendedor inválido');
        return;
      }

      console.log('Enviando atribuição de vendedor:', { 
        vendaId: changingVendedor.id, 
        novoVendedorId: vendedorId
      });
      
      await salesService.updateAtribuicaoVendedor(changingVendedor.id, vendedorId);
      
      alert('✅ Vendedor atualizado com sucesso!');
      await loadVendas();
      
      // Atualizar venda selecionada se for a mesma
      if (selectedVenda && selectedVenda.id === changingVendedor.id) {
        const vendaAtualizada = await salesService.getById(changingVendedor.id);
        setSelectedVenda(vendaAtualizada);
      }
      
      setChangingVendedor(null);
      setNovoVendedorId('');
    } catch (error) {
      console.error('Erro ao atualizar vendedor:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao atualizar vendedor';
      alert(`❌ ${errorMessage}`);
    }
  };
  
  const handleAtribuicaoBkoSubmit = async (e) => {
    e.preventDefault();
    try {
      // Se for string vazia ou não definido, enviar null explicitamente
      let tratadoPorId = null;
      
      // Verificar se novoTratadoPor tem valor válido
      // O select retorna string vazia '' quando "Limpar atribuição" é selecionado
      const valorTratado = novoTratadoPor;
      
      if (valorTratado && valorTratado.toString().trim() !== '' && valorTratado !== '0') {
        const parsedId = parseInt(valorTratado);
        if (!isNaN(parsedId) && parsedId > 0) {
          tratadoPorId = parsedId;
        }
      }
      // Se chegou até aqui sem atribuir valor, tratadoPorId continua null (limpar atribuição)
      
      console.log('Enviando atribuição de BKO:', { 
        vendaId: changingAtribuicao.id, 
        valorOriginal: valorTratado, 
        tratadoPorId,
        tipoOriginal: typeof valorTratado
      });
      
      await salesService.updateAtribuicaoBko(changingAtribuicao.id, tratadoPorId);
      
      alert('✅ Atribuição de BKO atualizada com sucesso!');
      await loadVendas();
      
      // Atualizar venda selecionada se for a mesma
      if (selectedVenda && selectedVenda.id === changingAtribuicao.id) {
        const vendaAtualizada = await salesService.getById(changingAtribuicao.id);
        setSelectedVenda(vendaAtualizada);
      }
      
      setChangingAtribuicao(null);
      setNovoTratadoPor('');
    } catch (error) {
      console.error('Erro ao atualizar atribuição de BKO:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao atualizar atribuição de BKO';
      alert(`❌ ${errorMessage}`);
    }
  };

  const editVenda = async (venda) => {
    try {
      const vendaCompleta = await salesService.getById(venda.id);
      // Manter a observação já salva. Zerar aqui fazia o update enviar "" e apagar a observação do pacote.
      setEditingVenda({ ...vendaCompleta, observacao_venda: vendaCompleta?.observacao_venda || '' });
    } catch (error) {
      console.error('Erro ao carregar venda para edição:', error);
      alert('Erro ao carregar venda para edição');
    }
  };

  const openChangeStatus = async (venda) => {
    setChangingStatus(venda);
    setNewStatus(venda.status);
    const dataInicial = venda.data_instalacao || venda.data_agendamento1 || getTodayBrasilia();
    const usaCamposInstalacao = venda.status === 'aguardando_instalacao' || venda.status === 'validacao_ok';
    setNewDataInstalacao(usaCamposInstalacao ? dataInicial : '');
    setNewNumeroOrdem(usaCamposInstalacao ? (venda.numero_ordem || '') : '');
    setNewDataConclusao(venda.status === 'concluido' ? (venda.data_conclusao || getTodayBrasilia()) : '');
  };

  const handleChangeStatus = async () => {
    if (!newStatus) {
      alert('Selecione um status');
      return;
    }
    if ((newStatus === 'aguardando_instalacao' || newStatus === 'validacao_ok') && !newDataInstalacao?.trim()) {
      alert('Informe a data da instalação para este status.');
      return;
    }
    if (newStatus === 'validacao_ok' && !newNumeroOrdem?.trim()) {
      alert('Informe o número da ordem para o status "Validação OK".');
      return;
    }
    if (newStatus === 'concluido' && !newDataConclusao?.trim()) {
      alert('Informe a data de conclusão para o status "Concluído".');
      return;
    }

    try {
      const requerInstalacao = newStatus === 'aguardando_instalacao' || newStatus === 'validacao_ok';
      const dataInstalacao = requerInstalacao ? newDataInstalacao : undefined;
      const numeroOrdem = requerInstalacao ? newNumeroOrdem : undefined;
      const dataConclusao = newStatus === 'concluido' ? newDataConclusao : undefined;
      await salesService.updateStatus(changingStatus.id, newStatus, '', dataInstalacao, numeroOrdem, dataConclusao, undefined, undefined, undefined, undefined, undefined, { preservarAtribuicaoBko: true });
      alert('✅ Status atualizado com sucesso!');
      closeModal();
      loadVendas();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('❌ Erro ao atualizar status: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  // Função para solicitar verificação de aceite
  const handleVerificarAceite = async (venda) => {
    if (verificandoAceite) return;

    if (!validarCooldownOuAlertar(user, venda.ultima_solicitacao_verificacao)) {
      return;
    }

    if (!window.confirm('Confirmar solicitação de verificação de aceite? A venda voltará para a fila de tratamento.')) {
      return;
    }

    setVerificandoAceite(true);
    try {
      await salesService.verificarAceite(venda.id);
      alert('✅ Solicitação enviada com sucesso! A venda voltou para a fila de tratamento.');
      // Recarregar a venda
      const vendaAtualizada = await salesService.getById(venda.id);
      setSelectedVenda(vendaAtualizada);
      loadVendas();
    } catch (error) {
      console.error('Erro ao solicitar verificação:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao solicitar verificação';
      alert('❌ ' + errorMsg);
    } finally {
      setVerificandoAceite(false);
    }
  };

  const openAlterarEmailAceiteModal = (venda) => {
    if (!validarCooldownOuAlertar(user, venda.ultima_solicitacao_verificacao)) return;
    setAlterarEmailVenda(venda);
    setNovoEmailAlterarAceite((venda.email || '').trim());
  };

  const handleSalvarAlterarEmailAceite = async (e) => {
    e.preventDefault();
    if (!alterarEmailVenda || salvandoEmailAceite) return;
    const emailTrim = novoEmailAlterarAceite.trim();
    if (!emailTrim) {
      alert('Informe o novo email.');
      return;
    }
    const emailBasico = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailBasico.test(emailTrim)) {
      alert('Informe um email válido.');
      return;
    }
    setSalvandoEmailAceite(true);
    try {
      await salesService.alterarEmailAceite(alterarEmailVenda.id, emailTrim);
      alert('✅ Email atualizado. A venda voltou para Pendente BKO.');
      const vendaAtualizada = await salesService.getById(alterarEmailVenda.id);
      setSelectedVenda(vendaAtualizada);
      setAlterarEmailVenda(null);
      setNovoEmailAlterarAceite('');
      loadVendas();
    } catch (error) {
      console.error('Erro ao alterar email:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao alterar email';
      alert('❌ ' + errorMsg);
    } finally {
      setSalvandoEmailAceite(false);
    }
  };

  const openAlterarCelularAceiteModal = (venda) => {
    if (!validarCooldownOuAlertar(user, venda.ultima_solicitacao_verificacao)) return;
    setAlterarCelularVenda(venda);
    setNovoCelularAlterarAceite(venda.telefone ? maskPhone(venda.telefone) : '');
  };

  const handleSalvarAlterarCelularAceite = async (e) => {
    e.preventDefault();
    if (!alterarCelularVenda || salvandoCelularAceite) return;
    const telefoneDigits = novoCelularAlterarAceite.replace(/\D/g, '');
    if (!telefoneDigits) {
      alert('Informe o novo celular.');
      return;
    }
    if (telefoneDigits.length < 10 || telefoneDigits.length > 11) {
      alert('Informe um celular válido (10 ou 11 dígitos).');
      return;
    }
    setSalvandoCelularAceite(true);
    try {
      await salesService.alterarCelularAceite(alterarCelularVenda.id, telefoneDigits);
      alert('✅ Celular atualizado. A venda voltou para Pendente BKO.');
      const vendaAtualizada = await salesService.getById(alterarCelularVenda.id);
      setSelectedVenda(vendaAtualizada);
      setAlterarCelularVenda(null);
      setNovoCelularAlterarAceite('');
      loadVendas();
    } catch (error) {
      console.error('Erro ao alterar celular:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao alterar celular';
      alert('❌ ' + errorMsg);
    } finally {
      setSalvandoCelularAceite(false);
    }
  };

  const handleVerificarBiometria = async (venda) => {
    if (verificandoBiometria) return;

    if (!validarCooldownOuAlertar(user, venda.ultima_devolucao_biometria)) {
      return;
    }

    if (!window.confirm('Confirmar solicitação de verificação de biometria? A venda voltará para a fila de tratamento.')) {
      return;
    }

    setVerificandoBiometria(true);
    try {
      await salesService.verificarBiometria(venda.id);
      alert('✅ Solicitação enviada com sucesso! A venda voltou para a fila de tratamento.');
      const vendaAtualizada = await salesService.getById(venda.id);
      setSelectedVenda(vendaAtualizada);
      loadVendas();
    } catch (error) {
      console.error('Erro ao solicitar verificação de biometria:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao solicitar verificação';
      alert('❌ ' + errorMsg);
    } finally {
      setVerificandoBiometria(false);
    }
  };

  const podeNotificarClienteRespondeuWhatsapp = (venda) => {
    if (!venda || venda.status !== 'aguardando_whatsapp' || !user) return false;
    if (!['vendedor', 'supervisor', 'admin', 'diretor'].includes(user.role)) return false;
    if (['admin', 'diretor'].includes(user.role)) return true;
    if (user.role === 'vendedor') return venda.vendedor_id === user.id;
    if (user.role === 'supervisor') return true;
    return false;
  };

  const podeNotificarClienteAtualizouSfa = (venda) => {
    if (!venda || venda.status !== 'aguardando_dados_sfa' || !user) return false;
    if (!['vendedor', 'supervisor', 'admin', 'diretor'].includes(user.role)) return false;
    if (['admin', 'diretor'].includes(user.role)) return true;
    if (user.role === 'vendedor') return venda.vendedor_id === user.id;
    if (user.role === 'supervisor') return true;
    return false;
  };

  const podeInformarCancelouOuReagendou = (venda) => {
    if (!venda || venda.status !== 'aguardando_can_ou_reag' || !user) return false;
    if (!['vendedor', 'supervisor', 'admin', 'diretor'].includes(user.role)) return false;
    if (['admin', 'diretor'].includes(user.role)) return true;
    if (user.role === 'vendedor') return venda.vendedor_id === user.id;
    if (user.role === 'supervisor') return true;
    return false;
  };

  const handleClienteRespondeuWhatsapp = async (venda) => {
    if (clienteRespondeuWhatsappId != null) return;
    if (!validarCooldownOuAlertar(user, venda.ultima_devolucao_whatsapp)) return;
    if (!window.confirm('Confirmar que o cliente já respondeu? A venda voltará para Pendente BKO e será registrado no histórico de tratamento.')) {
      return;
    }
    setClienteRespondeuWhatsappId(venda.id);
    try {
      await salesService.clienteRespondeuWhatsapp(venda.id);
      alert('✅ Venda retornou para Pendente BKO. O histórico foi atualizado com "Cliente já respondeu".');
      const vendaAtualizada = await salesService.getById(venda.id);
      if (selectedVenda?.id === venda.id) {
        setSelectedVenda(vendaAtualizada);
      }
      loadVendas();
      loadStats();
    } catch (error) {
      console.error('Erro ao notificar cliente respondeu:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao processar notificação';
      alert('❌ ' + errorMsg);
    } finally {
      setClienteRespondeuWhatsappId(null);
    }
  };

  const handleClienteAtualizouSfa = async (venda) => {
    if (clienteAtualizouSfaId != null) return;
    if (!window.confirm('Confirmar que o cliente informou que atualizou (SFA)? A venda voltará para Pendente BKO e será registrado no histórico de tratamento.')) {
      return;
    }
    setClienteAtualizouSfaId(venda.id);
    try {
      await salesService.clienteAtualizouSfa(venda.id);
      alert('✅ Venda retornou para Pendente BKO. O histórico foi atualizado com "Cliente informa que atualizou (SFA)".');
      const vendaAtualizada = await salesService.getById(venda.id);
      if (selectedVenda?.id === venda.id) {
        setSelectedVenda(vendaAtualizada);
      }
      loadVendas();
      loadStats();
    } catch (error) {
      console.error('Erro ao notificar cliente atualizou SFA:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao processar notificação';
      alert('❌ ' + errorMsg);
    } finally {
      setClienteAtualizouSfaId(null);
    }
  };

  const handleVendedorCancelouOuReagendou = async (venda) => {
    if (vendedorCancelouOuReagendouId != null) return;
    if (!window.confirm('Confirmar que já cancelou ou reagendou para a semana posterior? A venda voltará para Pendente BKO e será registrado no histórico de tratamento.')) {
      return;
    }
    setVendedorCancelouOuReagendouId(venda.id);
    try {
      await salesService.vendedorCancelouOuReagendou(venda.id);
      alert('✅ Venda retornou para Pendente BKO. O histórico foi atualizado com a confirmação de cancelamento/reagendamento.');
      const vendaAtualizada = await salesService.getById(venda.id);
      if (selectedVenda?.id === venda.id) {
        setSelectedVenda(vendaAtualizada);
      }
      loadVendas();
      loadStats();
    } catch (error) {
      console.error('Erro ao informar cancelamento/reagendamento:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao processar notificação';
      alert('❌ ' + errorMsg);
    } finally {
      setVendedorCancelouOuReagendouId(null);
    }
  };

  const handleDeleteVenda = async (venda) => {
    const clienteNome = venda.nome_cliente || venda.razao_social || `Venda #${venda.id}`;
    const confirmMessage = `Tem certeza que deseja excluir a venda "${clienteNome}"?\n\nEsta ação não pode ser desfeita!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await salesService.delete(venda.id);
      alert('✅ Venda excluída com sucesso!');
      loadVendas();
      loadStats();
    } catch (error) {
      console.error('Erro ao excluir venda:', error);
      alert('❌ Erro ao excluir venda: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleVoltarFila = async (venda) => {
    const clienteNome = venda.nome_cliente || venda.razao_social || `Venda #${venda.id}`;
    const confirmMessage = `Tem certeza que deseja voltar a venda "${clienteNome}" para a fila de tratamento?\n\nA venda voltará como "Pendente BKO" mantendo a atribuição atual.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Mudar status para pendente_bko mantendo a atribuição
      await salesService.updateStatus(venda.id, 'pendente_bko', '', undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, { preservarAtribuicaoBko: true });
      alert('✅ Venda voltou para a fila de tratamento com sucesso!');
      
      // Recarregar vendas e estatísticas
      await loadVendas();
      loadStats();
      
      // Se a venda estava sendo visualizada, atualizar também
      if (selectedVenda && selectedVenda.id === venda.id) {
        const vendaAtualizada = await salesService.getById(venda.id);
        setSelectedVenda(vendaAtualizada);
      }
    } catch (error) {
      console.error('Erro ao voltar venda à fila:', error);
      alert('❌ Erro ao voltar venda à fila: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const handleReenviarBiometria = async () => {
    if (!metodoReenvio) {
      alert('Selecione o método de reenvio (SMS, Email ou WhatsApp)');
      return;
    }

    const metodoTexto = {
      sms: 'SMS',
      email: 'Email',
      whatsapp: 'WhatsApp',
    }[metodoReenvio] || metodoReenvio;
    const observacao = `Solicitado reenvio de biometria via ${metodoTexto}`;
    
    try {
      await salesService.updateStatus(reenviandoBiometria.id, 'pendente_bko', observacao, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, { preservarAtribuicaoBko: true });
      alert(`✅ Solicitação de reenvio via ${metodoTexto} registrada com sucesso! A venda voltou para a fila do BKO.`);
      
      // Fechar modal e limpar estado
      setReenviandoBiometria(null);
      setMetodoReenvio('');
      
      // Recarregar vendas e estatísticas
      await loadVendas();
      loadStats();
      
      // Se a venda estava sendo visualizada, atualizar também
      if (selectedVenda && selectedVenda.id === reenviandoBiometria.id) {
        const vendaAtualizada = await salesService.getById(reenviandoBiometria.id);
        setSelectedVenda(vendaAtualizada);
      }
    } catch (error) {
      console.error('Erro ao solicitar reenvio de biometria:', error);
      alert('❌ Erro ao solicitar reenvio: ' + (error.response?.data?.error || 'Erro desconhecido'));
    }
  };

  const openExportModal = () => {
    const { mes, ano } = getCurrentMonthYearBrasilia();
    setExportPeriodoTipo('mes');
    setExportMes(mes);
    setExportAno(ano);
    setExportDataInicioDisplay('');
    setExportDataFimDisplay('');
    setExportDataInicio('');
    setExportDataFim('');
    setShowExportModal(true);
  };

  const closeExportModal = () => {
    if (exportLoading) return;
    setShowExportModal(false);
  };

  const handleExportDataChange = (field, rawValue) => {
    const masked = maskDateDDMMYYYY(rawValue);
    if (field === 'inicio') {
      setExportDataInicioDisplay(masked);
      setExportDataInicio(parseDateDDMMYYYY(masked) || '');
    } else {
      setExportDataFimDisplay(masked);
      setExportDataFim(parseDateDDMMYYYY(masked) || '');
    }
  };

  const buildExportFilters = (periodo) => {
    const filters = {};
    if (filtros.status_mode === 'include' && filtros.status_list && filtros.status_list.length > 0) {
      filters.status = filtros.status_list.join(',');
    } else if (filtros.status_mode === 'exclude' && filtros.status_list && filtros.status_list.length > 0) {
      filters.status_excluir = filtros.status_list.join(',');
    }
    if (filtros.vendedor_id && ['admin', 'diretor', 'supervisor'].includes(user?.role)) {
      filters.vendedor_id = filtros.vendedor_id;
    }
    if (filtros.supervisor_id && ['admin', 'diretor'].includes(user?.role)) {
      filters.supervisor_id = filtros.supervisor_id;
    }
    if (filtros.operadora_id && filtros.operadora_id.trim() !== '') {
      filters.operadora_id = filtros.operadora_id;
    }
    if (filtros.tipo_cliente && filtros.tipo_cliente.trim() !== '') {
      filters.tipo_cliente = filtros.tipo_cliente;
    }
    if (filtros.marca && filtros.marca.trim() !== '') {
      filters.marca = filtros.marca;
    }
    if (periodo.tipo === 'mes') {
      filters.mes = periodo.mes;
      filters.ano = periodo.ano;
    } else {
      filters.data_inicio = periodo.data_inicio;
      filters.data_fim = periodo.data_fim;
    }
    if (filtros.data_instalacao_inicio && filtros.data_instalacao_fim) {
      filters.data_instalacao_inicio = filtros.data_instalacao_inicio;
      filters.data_instalacao_fim = filtros.data_instalacao_fim;
    }
    return filters;
  };

  const handleExportVendas = async () => {
    if (exportPeriodoTipo === 'mes') {
      if (!exportMes || !exportAno) {
        alert('Selecione o mês e o ano para exportação.');
        return;
      }
    } else if (!exportDataInicio || !exportDataFim) {
      alert('Informe a data de início e fim no formato dd/mm/aaaa.');
      return;
    } else if (exportDataInicio > exportDataFim) {
      alert('A data de início não pode ser posterior à data de fim.');
      return;
    }

    const periodo = exportPeriodoTipo === 'mes'
      ? { tipo: 'mes', mes: exportMes, ano: exportAno }
      : { tipo: 'intervalo', data_inicio: exportDataInicio, data_fim: exportDataFim };

    setExportLoading(true);
    try {
      await salesService.exportSales(buildExportFilters(periodo));
      alert('✅ Vendas exportadas com sucesso!');
      setShowExportModal(false);
    } catch (error) {
      console.error('Erro ao exportar vendas:', error);
      alert('❌ Erro ao exportar vendas: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportVendasAguardandoInstalacao = async () => {
    if (exportLoading) return;
    setExportLoading(true);
    try {
      await salesService.exportSales({ status: 'aguardando_instalacao' });
      alert('✅ Vendas aguardando instalação exportadas com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar vendas aguardando instalação:', error);
      alert('❌ Erro ao exportar vendas aguardando instalação: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setExportLoading(false);
    }
  };

  const resetProcessarVendas = () => {
    setShowProcessarVendas(false);
    setProcessarStep('tipo');
    setProcessarTipo('');
    setProcessarAcao('');
    setProcessarLoading(false);
    if (processarFileInputRef.current) processarFileInputRef.current.value = '';
  };

  const abrirProcessarVendas = () => {
    setProcessarStep('tipo');
    setProcessarTipo('');
    setProcessarAcao('');
    setShowProcessarVendas(true);
  };

  const closeProcessarRelatorio = () => {
    setProcessarResultado(null);
    loadVendas();
    loadStats();
  };

  const getProcessarPlanilhaDesc = () => {
    if (processarTipo === 'cpf' && processarAcao === 'concluido') {
      return 'Coluna A: CPF | Coluna B: Data de conclusão | Coluna C: Código PAP';
    }
    if (processarTipo === 'cnpj' && processarAcao === 'concluido') {
      return 'Coluna A: CNPJ | Coluna B: Data de conclusão | Coluna C: Número da ordem';
    }
    if (processarTipo === 'cpf' && processarAcao === 'instalacao_cancelada') {
      return 'Coluna A: CPF | Coluna B: (ignorada) | Coluna C: Código PAP';
    }
    if (processarTipo === 'cnpj' && processarAcao === 'instalacao_cancelada') {
      return 'Coluna A: CNPJ | Coluna B: (ignorada) | Coluna C: Número da ordem';
    }
    return '';
  };

  const handleProcessarVendasFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !processarTipo || !processarAcao) return;
    setProcessarLoading(true);
    try {
      const data = await salesService.processarVendas(file, processarTipo, processarAcao);
      resetProcessarVendas();
      setProcessarResultado({ ...data, origem: 'vendas' });
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Erro ao processar planilha');
    } finally {
      setProcessarLoading(false);
    }
  };

  const resetProcessarPlanilha = () => {
    setShowProcessarPlanilha(false);
    setProcessarPlanilhaLoading(false);
    if (processarPlanilhaFileInputRef.current) processarPlanilhaFileInputRef.current.value = '';
  };

  const abrirProcessarPlanilha = () => {
    setShowProcessarPlanilha(true);
  };

  const handleProcessarPlanilhaFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setProcessarPlanilhaLoading(true);
    try {
      const data = await salesService.processarPlanilha(file);
      resetProcessarPlanilha();
      setProcessarResultado({ ...data, origem: 'planilha' });
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Erro ao processar planilha');
    } finally {
      setProcessarPlanilhaLoading(false);
    }
  };


  return (
    <Layout>
      <div className="vendas-page">
        {/* Header */}
        <div className="vendas-header">
          <div>
            <h1 className="card-title">📊 Vendas</h1>
          </div>
          <div className="header-actions">
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              ← Voltar
            </button>
            {user && ['admin', 'diretor'].includes(user.role) && (
              <>
                <button onClick={abrirProcessarVendas} className="btn btn-primary">
                  Processar Vendas
                </button>
                <button onClick={abrirProcessarPlanilha} className="btn btn-primary">
                  Processar Planilha
                </button>
                <button onClick={openExportModal} className="btn btn-success">
                  📥 Exportar Vendas
                </button>
                <button
                  onClick={handleExportVendasAguardandoInstalacao}
                  className="btn btn-success"
                  disabled={exportLoading}
                >
                  {exportLoading ? '⏳ Exportando...' : '📥 Exportar Aguardando Inst.'}
                </button>
              </>
            )}
            {user && user.role !== 'backoffice' && user.role !== 'supervisor_bko' && (
              <button onClick={() => navigate('/vendas/consulta-viabilidade')} className="btn btn-primary">
                ➕ Nova Venda
              </button>
            )}
          </div>
        </div>

        {/* Estatísticas */}
        <div className="vendas-stats">
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total de Vendas</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.pendentesBko}</div>
            <div className="stat-label">Pendente BKO</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.pendentesVendedor}</div>
            <div className="stat-label">Pendente Vendedor</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.pendentesSistemica}</div>
            <div className="stat-label">Pendente Sistêmica</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.aguardandoInstalacao}</div>
            <div className="stat-label">Aguardando Instalação</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.concluidas}</div>
            <div className="stat-label">Concluídas</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.canceladas}</div>
            <div className="stat-label">Canceladas</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="filter-section">
          <h3 style={{ marginBottom: '1rem', color: 'var(--azul-dvs)' }}>🔍 Filtros</h3>
          <div className="filter-grid">
            {/* Linha 1: Mês + Filtro por status */}
            <div className="filter-grid-row filter-grid-row--status">
              <div className="filter-group" style={{ minWidth: '140px' }}>
                <label htmlFor="filtro-mes">Mês</label>
                <select
                  id="filtro-mes"
                  value={filtros.mes && filtros.ano ? `${filtros.mes}|${filtros.ano}` : ''}
                  onChange={(e) => handleMonthFilterChange(e.target.value)}
                >
                  {opcoesMesesVendas.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group" style={{ flex: 1, minWidth: 0 }}>
                <label>Filtro por status</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="status_mode"
                      checked={filtros.status_mode === ''}
                      onChange={() => handleFilterChange('status_mode', '')}
                    />
                    <span>Todos os status</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="status_mode"
                      checked={filtros.status_mode === 'include'}
                      onChange={() => handleFilterChange('status_mode', 'include')}
                    />
                    <span>Incluir apenas estes</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="status_mode"
                      checked={filtros.status_mode === 'exclude'}
                      onChange={() => handleFilterChange('status_mode', 'exclude')}
                    />
                    <span>Excluir estes</span>
                  </label>
                </div>
                {(filtros.status_mode === 'include' || filtros.status_mode === 'exclude') && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', padding: '0.75rem', background: 'var(--cinza-escuro-2)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                    {STATUS_OPTIONS.map(opt => (
                      <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input
                          type="checkbox"
                          checked={(filtros.status_list || []).includes(opt.value)}
                          onChange={() => handleStatusListToggle(opt.value)}
                        />
                        <span>{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              </div>
            </div>

            {/* Linha 2: Vendedor/Supervisor + datas */}
            <div className="filter-grid-row">
              {isAdminOrDiretor && (
                <div className="filter-group">
                  <label htmlFor="filtro-supervisor">Supervisor</label>
                  <select
                    id="filtro-supervisor"
                    value={filtros.supervisor_id}
                    onChange={(e) => {
                      const sid = e.target.value;
                      setCurrentPage(1);
                      setFiltros((prev) => {
                        const next = { ...prev, supervisor_id: sid };
                        if (sid && prev.vendedor_id) {
                          const vend = vendedores.find((v) => String(v.id) === String(prev.vendedor_id));
                          const stillInTeam =
                            vend &&
                            (String(vend.id) === String(sid) || String(vend.supervisor_id) === String(sid));
                          if (!stillInTeam) next.vendedor_id = '';
                        }
                        return next;
                      });
                    }}
                  >
                    <option value="">Todos os supervisores</option>
                    {supervisores.map((sup) => (
                      <option key={sup.id} value={sup.id}>
                        {sup.nome || sup.username}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {(isAdminOrDiretor || isSupervisor) ? (
                <div className="filter-group">
                  <label htmlFor="filtro-vendedor">Vendedor</label>
                  <select
                    id="filtro-vendedor"
                    value={filtros.vendedor_id}
                    onChange={(e) => handleFilterChange('vendedor_id', e.target.value)}
                  >
                    <option value="">{isSupervisor ? 'Toda a equipe' : 'Todos os vendedores'}</option>
                    {(filtros.supervisor_id
                      ? vendedores.filter(
                          (v) =>
                            String(v.id) === String(filtros.supervisor_id) ||
                            String(v.supervisor_id) === String(filtros.supervisor_id)
                        )
                      : vendedores
                    ).map((vendedor) => (
                      <option key={vendedor.id} value={vendedor.id}>
                        {vendedor.nome || vendedor.username}
                        {isSupervisor && Number(vendedor.id) === Number(user?.id) ? ' (eu)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div />
              )}
              <div className="filter-group">
                <label htmlFor="filtro-operadora">Operadora</label>
                <select
                  id="filtro-operadora"
                  value={filtros.operadora_id}
                  onChange={(e) => handleFilterChange('operadora_id', e.target.value)}
                >
                  <option value="">Todas as operadoras</option>
                  {operadoras.filter(op => op.ativo !== false).map(op => (
                    <option key={op.id} value={op.id}>
                      {op.nome_display || op.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="filtro-tipo-cliente">Tipo</label>
                <select
                  id="filtro-tipo-cliente"
                  value={filtros.tipo_cliente}
                  onChange={(e) => handleFilterChange('tipo_cliente', e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="cpf">CPF</option>
                  <option value="cnpj">CNPJ</option>
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="filtro-marca">Marca</label>
                <select
                  id="filtro-marca"
                  value={filtros.marca}
                  onChange={(e) => handleFilterChange('marca', e.target.value)}
                  disabled={filtros.tipo_cliente === 'cnpj'}
                >
                  <option value="">Todas</option>
                  <option value="extk">EXTK</option>
                  <option value="onda">ONDA TELECOM</option>
                  <option value="vision">VISION TELECOM</option>
                </select>
              </div>
              <div className="filter-group">
                <label htmlFor="filtro-data-inicio">Data Início</label>
              <input
                type="text"
                inputMode="numeric"
                id="filtro-data-inicio"
                placeholder="dd/mm/aaaa"
                maxLength={10}
                value={dataInicioDisplay}
                onChange={(e) => handleDataFiltroChange('data_inicio', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="filtro-data-fim">Data Fim</label>
              <input
                type="text"
                inputMode="numeric"
                id="filtro-data-fim"
                placeholder="dd/mm/aaaa"
                maxLength={10}
                value={dataFimDisplay}
                onChange={(e) => handleDataFiltroChange('data_fim', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="filtro-data-instalacao-inicio">Data Instalação Início</label>
              <input
                type="text"
                inputMode="numeric"
                id="filtro-data-instalacao-inicio"
                placeholder="dd/mm/aaaa"
                maxLength={10}
                value={dataInstalacaoInicioDisplay}
                onChange={(e) => handleDataFiltroChange('data_instalacao_inicio', e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="filtro-data-instalacao-fim">Data Instalação Fim</label>
              <input
                type="text"
                inputMode="numeric"
                id="filtro-data-instalacao-fim"
                placeholder="dd/mm/aaaa"
                maxLength={10}
                value={dataInstalacaoFimDisplay}
                onChange={(e) => handleDataFiltroChange('data_instalacao_fim', e.target.value)}
              />
            </div>
            </div>

            {/* Linha 3: Busca */}
            <div className="filter-grid-row filter-grid-row--busca">
              <div className="filter-group">
                <label htmlFor="filtro-busca">Buscar por CPF/CNPJ, Nome, Nº da ordem ou Código PAP</label>
                <input
                  type="text"
                  id="filtro-busca"
                value={filtros.busca}
                onChange={(e) => handleFilterChange('busca', e.target.value)}
                placeholder="Digite CPF/CNPJ, nome, nº da ordem ou código PAP..."
              />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
            <button
              onClick={handleClearFilters}
              className="btn"
              style={{ 
                backgroundColor: '#666',
                color: 'white',
                padding: '0.5rem 1.5rem',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🗑️ Limpar Filtros
            </button>
            <button
              onClick={handleApplyFilters}
              className="btn btn-primary"
              style={{ 
                padding: '0.5rem 1.5rem',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔍 Aplicar Filtros
            </button>
          </div>
        </div>

        {/* Tabela de Vendas */}
        {loading ? (
          <div className="loading">Carregando vendas...</div>
        ) : filteredVendas.length === 0 ? (
          <div className="no-vendas">
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
            <h3>Nenhuma venda encontrada</h3>
            <p>Comece criando sua primeira venda</p>
            {user && user.role !== 'backoffice' && user.role !== 'supervisor_bko' && (
              <button
                onClick={() => navigate('/vendas/consulta-viabilidade')}
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
              >
                ➕ Nova Venda
              </button>
            )}
          </div>
        ) : (
          <div className="vendas-table">
            <div className="table-header">
              <div>ID</div>
              <div>Cliente</div>
              <div>Documento</div>
              <div>Telefone</div>
              <div>Valor</div>
              <div>Status</div>
              <div>Vendedor</div>
              <div>Data Criação</div>
              <div>Ações</div>
            </div>
            
            {filteredVendas.map((venda) => (
              <div
                key={venda.id}
                className={`table-row${
                  isAdminOrDiretor
                  && venda.tipo_cliente === 'cpf'
                  && venda.cpf_tinha_cnpj_disponivel
                    ? ' row-cpf-cnpj-disponivel'
                    : ''
                }`}
              >
                <div>{venda.id}</div>
                <div>
                  {venda.tipo_cliente === 'cnpj' ? (
                    <>
                      <strong>{venda.razao_social || venda.nome_cliente || 'N/A'}</strong>
                      {venda.nome_cliente && venda.nome_cliente !== venda.razao_social && (
                        <><br /><small style={{ color: '#888' }}>📋 {venda.nome_cliente}</small></>
                      )}
                    </>
                  ) : (
                    <strong>{venda.nome_cliente || 'N/A'}</strong>
                  )}
                  <br />
                  <small style={{ color: '#666' }}>
                    {formatDocumento(venda.documento)}
                    {venda.tipo_cliente === 'cnpj' ? (
                      <span className="badge-cnpj">CNPJ</span>
                    ) : (
                      <>
                        <span className="badge-cpf">CPF</span>
                        {isAdminOrDiretor && venda.cpf_tinha_cnpj_disponivel && (
                          <span
                            className="badge-cnpj-disponivel"
                            title={parseCnpjsDisponiveis(venda).map((e) => `${formatDocumento(e.cnpj)} - ${e.razao_social}`).join('\n')}
                          >
                            CNPJ disponível
                          </span>
                        )}
                      </>
                    )}
                  </small>
                  {venda.email && (
                    <><br /><small style={{ color: '#888' }}>📧 {venda.email}</small></>
                  )}
                  {venda.telefone && (
                    <><br /><small style={{ color: '#888' }}>📱 {formatPhone(venda.telefone)}</small></>
                  )}
                  {venda.logradouro && (
                    <><br /><small style={{ color: '#888' }}>
                      📍 {venda.logradouro}, {venda.numero}<br />
                      {venda.bairro} - {venda.cidade}/{venda.estado}
                    </small></>
                  )}
                </div>
                <div>{formatDocumento(venda.documento)}</div>
                <div>{formatPhone(venda.telefone)}</div>
                <div>
                  {(user?.role === 'backoffice' || user?.role === 'supervisor_bko') && venda.status === 'concluido'
                    ? '—'
                    : `R$ ${parseFloat(venda.valor_total || 0).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}`}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                  {isValidacaoOkRobo(venda) ? (
                    <small style={{ color: 'var(--texto-secundario)', whiteSpace: 'nowrap' }}>
                      robo
                    </small>
                  ) : (venda.status === 'aguardando_instalacao' || venda.status === 'validacao_ok') && venda.data_instalacao && (
                    <small style={{ color: 'var(--texto-secundario)', whiteSpace: 'nowrap' }}>
                      {formatDateOnly(venda.data_instalacao)}
                      {venda.numero_ordem ? ` · Ordem: ${venda.numero_ordem}` : ''}
                    </small>
                  )}
                  {venda.status === 'concluido' && venda.data_conclusao && (
                    <small style={{ color: 'var(--texto-secundario)', whiteSpace: 'nowrap' }}>
                      Concluído: {formatDateOnly(venda.data_conclusao)}
                    </small>
                  )}
                  <span className={`status-badge ${getStatusClass(venda.status)}`}>
                    {getStatusText(venda.status)}
                  </span>
                </div>
                <div>
                  <strong>{venda.vendedor?.nome || 'N/A'}</strong>
                  {user?.role === 'supervisor' && venda.vendedor_id != null && (
                    <span
                      style={{
                        display: 'block',
                        marginTop: '0.35rem',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: venda.vendedor_id === user.id ? 'var(--azul-dvs, #2196f3)' : '#9ca3af'
                      }}
                    >
                      {venda.vendedor_id === user.id ? 'Minha venda' : 'Venda da equipe'}
                    </span>
                  )}
                </div>
                <div>
                  <strong>{formatDate(venda.created_at)}</strong>
                  <br />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    {new Date(venda.created_at).toLocaleTimeString('pt-BR', {
                      timeZone: 'America/Sao_Paulo',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </small>
                </div>
                <div className="action-buttons">
                  <button
                    onClick={() => viewDetails(venda.id)}
                    className="btn-small btn-view"
                  >
                    Ver
                  </button>
                  {podeNotificarClienteRespondeuWhatsapp(venda) && (
                    <ClienteRespondeuWhatsappButton
                      venda={venda}
                      user={user}
                      onConfirmar={handleClienteRespondeuWhatsapp}
                      processando={clienteRespondeuWhatsappId === venda.id}
                    />
                  )}
                  {podeNotificarClienteAtualizouSfa(venda) && (
                    <button
                      type="button"
                      onClick={() => handleClienteAtualizouSfa(venda)}
                      className="btn-small"
                      disabled={clienteAtualizouSfaId != null}
                      style={{ background: '#3f51b5', color: '#fff' }}
                      title="Registra que o cliente informou que atualizou (SFA); a venda volta para Pendente BKO com registro no histórico."
                    >
                      {clienteAtualizouSfaId === venda.id ? '...' : 'Cliente atualizou SFA'}
                    </button>
                  )}
                  {podeInformarCancelouOuReagendou(venda) && (
                    <button
                      type="button"
                      onClick={() => handleVendedorCancelouOuReagendou(venda)}
                      className="btn-small"
                      disabled={vendedorCancelouOuReagendouId != null}
                      style={{ background: '#e65100', color: '#fff' }}
                      title="Informa ao BKO que já cancelou ou reagendou para a semana posterior; a venda volta para Pendente BKO com registro no histórico."
                    >
                      {vendedorCancelouOuReagendouId === venda.id ? '...' : 'Já cancelei/reagendei'}
                    </button>
                  )}
                  {(
                    // Admin e Diretor podem editar qualquer venda
                    ['admin', 'diretor'].includes(user?.role) ||
                    // Vendedor pode editar apenas vendas próprias com status pendente_vendedor
                    (user?.role === 'vendedor' && venda.status === 'pendente_vendedor' && venda.vendedor_id === user.id) ||
                    // Supervisor: pendente_vendedor — próprias vendas ou da equipe (API valida)
                    (user?.role === 'supervisor' && venda.status === 'pendente_vendedor')
                  ) && (
                    <button
                      onClick={() => editVenda(venda)}
                      className="btn-small btn-edit"
                    >
                      Editar
                    </button>
                  )}
                  {['admin', 'diretor'].includes(user?.role) && (
                    <button
                      onClick={() => openChangeStatus(venda)}
                      className="btn-small"
                      style={{ background: '#607d8b', color: '#fff' }}
                    >
                      Status
                    </button>
                  )}
                  {/* Botão Voltar à Fila - Backoffice pode usar quando a venda está atribuída a ele */}
                  {(user?.role === 'backoffice' && venda.tratado_por === user.id) && (
                    <button
                      onClick={() => handleVoltarFila(venda)}
                      className="btn-small"
                      style={{ background: '#ff9800', color: '#fff' }}
                      title="Voltar esta venda para a fila de tratamento"
                    >
                      🔄 Voltar à Fila
                    </button>
                  )}
                  {['admin', 'diretor'].includes(user?.role) && (
                    <button
                      onClick={() => handleDeleteVenda(venda)}
                      className="btn-small"
                      style={{ background: '#d32f2f', color: '#fff' }}
                    >
                      🗑️ Excluir
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Controles de Paginação */}
        {!loading && filteredVendas.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'var(--cinza-escuro-2)',
            borderRadius: '8px',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <span style={{ color: 'var(--texto-primario)' }}>
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, total)} de {total} vendas
              </span>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{
                  padding: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--cinza-escuro-1)',
                  color: 'var(--texto-primario)',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10 por página</option>
                <option value={20}>20 por página</option>
                <option value={50}>50 por página</option>
                <option value={100}>100 por página</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === 1 ? 'var(--cinza-escuro-2)' : 'var(--primary)',
                  color: currentPage === 1 ? 'var(--texto-secundario)' : 'var(--btn-on-primary)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                ⏮️ Primeira
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === 1 ? 'var(--cinza-escuro-2)' : 'var(--primary)',
                  color: currentPage === 1 ? 'var(--texto-secundario)' : 'var(--btn-on-primary)',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                ⬅️ Anterior
              </button>
              
              <span style={{ 
                padding: '0.5rem 1rem',
                color: 'var(--texto-primario)',
                fontWeight: 'bold'
              }}>
                Página {currentPage} de {totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === totalPages ? 'var(--cinza-escuro-2)' : 'var(--primary)',
                  color: currentPage === totalPages ? 'var(--texto-secundario)' : 'var(--btn-on-primary)',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                Próxima ➡️
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  border: '1px solid var(--border-color)',
                  background: currentPage === totalPages ? 'var(--cinza-escuro-2)' : 'var(--primary)',
                  color: currentPage === totalPages ? 'var(--texto-secundario)' : 'var(--btn-on-primary)',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.7 : 1,
                  fontWeight: 600
                }}
              >
                Última ⏭️
              </button>
            </div>
          </div>
        )}

        {/* Modal de detalhes */}
        {selectedVenda && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-venda">
                <h2>👁️ Detalhes da Venda #{selectedVenda.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <div className="modal-body-venda">
                {/* Observações internas (BKO/Diretoria) - apenas admin, diretor, supervisor_bko */}
                {['admin', 'diretor', 'supervisor_bko'].includes(user?.role) && (
                  <div className="info-section" style={{ marginBottom: '1.5rem', border: '1px solid var(--azul-dvs)', borderRadius: '8px', padding: '1rem', background: 'var(--cinza-escuro-2)' }}>
                    <h4>📋 Observações internas (BKO/Diretoria)</h4>
                    {selectedVenda.observacoes_supervisao && parseObservacoesSupervisao(selectedVenda.observacoes_supervisao).length > 0 ? (
                      <div style={{ maxHeight: '180px', overflowY: 'auto', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                        {parseObservacoesSupervisao(selectedVenda.observacoes_supervisao).map((o, idx) => (
                          <div key={idx} style={{ marginBottom: '0.5rem', padding: '0.35rem 0', borderBottom: '1px solid var(--border-color)' }}>
                            {o.dataHora ? <><span style={{ color: 'var(--azul-dvs)', fontWeight: 600 }}>[{o.dataHora}] {o.usuario}:</span> {o.texto}</> : o.texto}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: 'var(--text-muted)', marginBottom: '0.75rem', fontSize: '0.9rem' }}>Nenhuma observação ainda.</p>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <textarea
                        value={novaObservacaoSupervisao}
                        onChange={(e) => setNovaObservacaoSupervisao(e.target.value)}
                        placeholder="Nova observação..."
                        rows={2}
                        style={{ flex: 1, resize: 'vertical', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                      />
                      <button
                        type="button"
                        onClick={handleAddObservacaoSupervisao}
                        disabled={!novaObservacaoSupervisao.trim() || addingObservacaoSupervisao}
                        className="btn btn-primary"
                        style={{ alignSelf: 'flex-end' }}
                      >
                        {addingObservacaoSupervisao ? '...' : 'Adicionar'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Informações do Cliente */}
                <div className="info-section">
                  <h4>👤 Informações do Cliente</h4>
                  {isAdminOrDiretor && selectedVenda.tipo_cliente === 'cpf' && selectedVenda.cpf_tinha_cnpj_disponivel && (
                    <div className="alerta-cnpj-disponivel">
                      <strong>⚠️ Venda cadastrada como CPF com CNPJ disponível</strong>
                      <ul>
                        {parseCnpjsDisponiveis(selectedVenda).map((empresa) => (
                          <li key={empresa.cnpj}>
                            {formatDocumento(empresa.cnpj)} — {empresa.razao_social}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Nome/Razão Social</div>
                      <div className="info-value">
                        {selectedVenda.tipo_cliente === 'cnpj' 
                          ? (selectedVenda.razao_social || selectedVenda.nome_cliente)
                          : selectedVenda.nome_cliente
                        }
                      </div>
                    </div>
                    {selectedVenda.tipo_cliente === 'cnpj' && selectedVenda.nome_cliente && (
                      <div className="info-item">
                        <div className="info-label">Nome do Cliente</div>
                        <div className="info-value">{selectedVenda.nome_cliente}</div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Tipo</div>
                      <div className="info-value">
                        {selectedVenda.tipo_cliente === 'cpf' ? 'CPF' : 'CNPJ'}
                      </div>
                    </div>
                    {selectedVenda.tipo_cliente === 'cpf' && (
                      <div className="info-item">
                        <div className="info-label">Marca</div>
                        <div className="info-value">{formatMarca(selectedVenda.marca)}</div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Documento</div>
                      <div className="info-value">{formatDocumento(selectedVenda.documento)}</div>
                    </div>
                    {selectedVenda.cpf && (
                      <div className="info-item">
                        <div className="info-label">CPF</div>
                        <div className="info-value">{formatDocumento(selectedVenda.cpf)}</div>
                      </div>
                    )}
                    {selectedVenda.rg && (
                      <div className="info-item">
                        <div className="info-label">RG</div>
                        <div className="info-value">{selectedVenda.rg}</div>
                      </div>
                    )}
                    {selectedVenda.nome_mae && (
                      <div className="info-item">
                        <div className="info-label">Nome da Mãe</div>
                        <div className="info-value">{selectedVenda.nome_mae}</div>
                      </div>
                    )}
                    {selectedVenda.data_nascimento && (
                      <div className="info-item">
                        <div className="info-label">Data de Nascimento</div>
                        <div className="info-value">{formatDateOnly(selectedVenda.data_nascimento)}</div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Telefone</div>
                      <div className="info-value">{formatPhone(selectedVenda.telefone)}</div>
                    </div>
                    {selectedVenda.telefone2 && (
                      <div className="info-item">
                        <div className="info-label">Telefone 2</div>
                        <div className="info-value">{formatPhone(selectedVenda.telefone2)}</div>
                      </div>
                    )}
                    {selectedVenda.email && (
                      <div className="info-item">
                        <div className="info-label">Email</div>
                        <div className="info-value">{selectedVenda.email}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Dados PAP do vendedor — quando houver código PAP */}
                {String(selectedVenda.vendedor_codigo_pap || '').trim() && (
                  <div className="info-section">
                    <h4>📋 Dados PAP do Vendedor</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <div className="info-label">Código PAP</div>
                        <div className="info-value" style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                          {selectedVenda.vendedor_codigo_pap}
                        </div>
                      </div>
                      <div className="info-item">
                        <div className="info-label">Login PAP</div>
                        <div className="info-value">{selectedVenda.vendedor_login_pap || '—'}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Endereço */}
                <div className="info-section">
                  <h4>📍 Endereço</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">CEP</div>
                      <div className="info-value">{selectedVenda.cep}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Logradouro</div>
                      <div className="info-value">{selectedVenda.logradouro}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Número</div>
                      <div className="info-value">{selectedVenda.numero}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Bairro</div>
                      <div className="info-value">{selectedVenda.bairro}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Cidade</div>
                      <div className="info-value">{selectedVenda.cidade}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Estado</div>
                      <div className="info-value">
                        {selectedVenda.estado_completo} ({selectedVenda.estado})
                      </div>
                    </div>
                    {selectedVenda.complemento1 && (
                      <div className="info-item">
                        <div className="info-label">Complemento 1</div>
                        <div className="info-value">{selectedVenda.complemento1}</div>
                      </div>
                    )}
                    {selectedVenda.complemento2 && (
                      <div className="info-item">
                        <div className="info-label">Complemento 2</div>
                        <div className="info-value">{selectedVenda.complemento2}</div>
                      </div>
                    )}
                    {selectedVenda.complemento3 && (
                      <div className="info-item">
                        <div className="info-label">Complemento 3</div>
                        <div className="info-value">{selectedVenda.complemento3}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Agendamento */}
                <div className="info-section">
                  <h4>📅 Agendamento de Instalação</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Data Opção 1</div>
                      <div className="info-value">
                        {formatDateOnly(selectedVenda.data_agendamento1)} - 
                        {selectedVenda.periodo_agendamento1?.toUpperCase()}
                      </div>
                    </div>
                    {selectedVenda.data_agendamento2 && (
                      <div className="info-item">
                        <div className="info-label">Data Opção 2</div>
                        <div className="info-value">
                          {formatDateOnly(selectedVenda.data_agendamento2)} - 
                          {selectedVenda.periodo_agendamento2?.toUpperCase()}
                        </div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Aceita Antecipação</div>
                      <div className="info-value">
                        {selectedVenda.aceita_antecipacao ? 'Sim' : 'Não'}
                      </div>
                    </div>
                    {selectedVenda.observacao_instalacao && (
                      <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                        <div className="info-label">Observação sobre Instalação</div>
                        <div className="info-value">{selectedVenda.observacao_instalacao}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Financeiro */}
                <div className="info-section">
                  <h4>💰 Informações Financeiras</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Valor Total</div>
                      <div className="info-value" style={{ color: 'var(--azul-dvs)', fontWeight: '600', fontSize: '1.125rem' }}>
                        {(user?.role === 'backoffice' || user?.role === 'supervisor_bko') && selectedVenda.status === 'concluido'
                          ? '—'
                          : `R$ ${parseFloat(selectedVenda.valor_total || 0).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}`}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Data de Vencimento</div>
                      <div className="info-value">Dia {selectedVenda.data_vencimento}</div>
                    </div>
                  </div>
                </div>

                {/* Observações */}
                {selectedVenda.observacao_venda && (
                  <div className="info-section">
                    <h4>📝 Observações da Venda</h4>
                    <div className="observacao-venda-value">
                      {selectedVenda.observacao_venda}
                    </div>
                  </div>
                )}

                {/* Histórico de Observações de Tratamento e Troca de Status */}
                {selectedVenda.observacao_tratamento && (
                  <div className="info-section">
                    <h4>📋 Histórico de Tratamento e Mudanças de Status</h4>
                    <div className="historico-tratamento-container">
                      {parseObservacoesTratamento(selectedVenda.observacao_tratamento).map((obs, index) => (
                        <div
                          key={index}
                          className={`observacao-item ${obs.tipo === 'mudanca_status' ? 'mudanca-status' : ''}`}
                        >
                          {obs.tipo === 'mudanca_status' ? (
                            <>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                marginBottom: '0.75rem'
                              }}>
                                <span style={{ 
                                  fontSize: '1.2rem',
                                  filter: 'drop-shadow(0 0 4px rgba(6, 102, 255, 0.5))'
                                }}>🔄</span>
                                <span style={{ 
                                  fontWeight: '600', 
                                  color: 'var(--azul-dvs)',
                                  fontSize: '0.95rem'
                                }}>
                                  Mudança de Status
                                </span>
                                {obs.dataHora && (
                                  <span style={{ 
                                    marginLeft: 'auto',
                                    fontSize: '0.85rem',
                                    color: 'var(--texto-secundario)',
                                    opacity: 0.8
                                  }}>
                                    {obs.dataHora}
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.75rem',
                                flexWrap: 'wrap'
                              }}>
                                <div style={{
                                  padding: '0.4rem 0.8rem',
                                  background: 'rgba(255, 152, 0, 0.2)',
                                  border: '1px solid rgba(255, 152, 0, 0.4)',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  color: '#ff9800'
                                }}>
                                  {obs.statusAnterior}
                                </div>
                                <span style={{ color: 'var(--texto-secundario)' }}>→</span>
                                <div style={{
                                  padding: '0.4rem 0.8rem',
                                  background: 'rgba(6, 102, 255, 0.2)',
                                  border: '1px solid rgba(6, 102, 255, 0.4)',
                                  borderRadius: '6px',
                                  fontSize: '0.85rem',
                                  color: 'var(--azul-dvs)',
                                  fontWeight: '600'
                                }}>
                                  {obs.statusNovo}
                                </div>
                              </div>
                              {obs.usuario && (
                                <div style={{ 
                                  marginTop: '0.75rem',
                                  fontSize: '0.85rem',
                                  color: 'var(--texto-secundario)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  <span>👤</span>
                                  <span>Por: <strong>{obs.usuario}</strong></span>
                                </div>
                              )}
                            </>
                          ) : obs.tipo === 'observacao' ? (
                            <>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                marginBottom: '0.75rem'
                              }}>
                                <span style={{ fontSize: '1.1rem' }}>💬</span>
                                <span style={{ 
                                  fontWeight: '600', 
                                  color: 'var(--texto-primario)',
                                  fontSize: '0.95rem'
                                }}>
                                  Observação
                                </span>
                                {obs.dataHora && (
                                  <span style={{ 
                                    marginLeft: 'auto',
                                    fontSize: '0.85rem',
                                    color: 'var(--texto-secundario)',
                                    opacity: 0.8
                                  }}>
                                    {obs.dataHora}
                                  </span>
                                )}
                              </div>
                              <div style={{ 
                                color: 'var(--texto-primario)',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                              }}>
                                {obs.texto}
                              </div>
                              {obs.usuario && (
                                <div style={{ 
                                  marginTop: '0.75rem',
                                  fontSize: '0.85rem',
                                  color: 'var(--texto-secundario)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  <span>👤</span>
                                  <span>Por: <strong>{obs.usuario}</strong></span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ 
                              color: 'var(--texto-primario)',
                              lineHeight: '1.6',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}>
                              {obs.texto}
                            </div>
                          )}
                        </div>
                      ))}
                      {parseObservacoesTratamento(selectedVenda.observacao_tratamento).length === 0 && (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '2rem',
                          color: 'var(--texto-secundario)',
                          fontStyle: 'italic'
                        }}>
                          Nenhuma observação de tratamento registrada
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Imagem de Cancelamento/Sem Viabilidade */}
                {(selectedVenda.imagem_anexo || selectedVenda.motivo_imagem) && (
                  <div className="info-section">
                    <h4>🖼️ {selectedVenda.status === 'cancelado' ? 'Cancelamento' : 'Sem Viabilidade'}</h4>
                    {selectedVenda.motivo_imagem && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div className="info-label">Motivo:</div>
                        <div style={{ 
                          marginTop: '0.5rem',
                          padding: '1rem',
                          background: 'var(--bg-secondary)',
                          borderRadius: '6px',
                          color: 'var(--texto-primario)',
                          lineHeight: '1.6'
                        }}>
                          {selectedVenda.motivo_imagem}
                        </div>
                      </div>
                    )}
                    {selectedVenda.imagem_anexo && (
                      <div>
                        <div className="info-label">Comprovante:</div>
                        <div style={{ marginTop: '0.5rem' }}>
                          <a 
                            href={`${process.env.REACT_APP_API_URL || 'http://localhost:9040'}${selectedVenda.imagem_anexo}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-block',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              border: '1px solid var(--border-color)'
                            }}
                          >
                            <img
                              src={`${process.env.REACT_APP_API_URL || 'http://localhost:9040'}${selectedVenda.imagem_anexo}`}
                              alt="Comprovante"
                              style={{ 
                                maxWidth: '100%',
                                maxHeight: '300px',
                                display: 'block',
                                objectFit: 'contain'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentNode.innerHTML = '<div style="padding: 1rem; background: var(--bg-secondary); border-radius: 6px; color: var(--texto-secundario);">Imagem não disponível</div>';
                              }}
                            />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Status */}
                <div className="info-section">
                  <h4>ℹ️ Status e Informações</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Status</div>
                      <div className="info-value">
                        {isValidacaoOkRobo(selectedVenda) ? (
                          <>
                            <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                              Data instalação: robo
                            </div>
                            <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                              Ordem: robo
                            </div>
                          </>
                        ) : (
                          <>
                            {(selectedVenda.status === 'aguardando_instalacao' || selectedVenda.status === 'validacao_ok') && selectedVenda.data_instalacao && (
                              <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                Data instalação: {formatDateOnly(selectedVenda.data_instalacao)}
                              </div>
                            )}
                            {(selectedVenda.status === 'aguardando_instalacao' || selectedVenda.status === 'validacao_ok') && selectedVenda.numero_ordem && (
                              <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                                Ordem: {selectedVenda.numero_ordem}
                              </div>
                            )}
                          </>
                        )}
                        {selectedVenda.status === 'concluido' && selectedVenda.data_conclusao && (
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                            Concluído em: {formatDateOnly(selectedVenda.data_conclusao)}
                          </div>
                        )}
                        <span className={`status-badge ${getStatusClass(selectedVenda.status)}`}>
                          {getStatusText(selectedVenda.status)}
                        </span>
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Vendedor</div>
                      <div className="info-value">{selectedVenda.vendedor?.nome || 'N/A'}</div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Cadastrado em</div>
                      <div className="info-value">{formatDateTime(selectedVenda.created_at)}</div>
                    </div>
                    {selectedVenda.tratado_por && (
                      <>
                        <div className="info-item">
                          <div className="info-label">Tratado por</div>
                          <div className="info-value">{selectedVenda.tratador?.nome}</div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">Tratado em</div>
                          <div className="info-value">{formatDateTime(selectedVenda.tratado_em)}</div>
                        </div>
                      </>
                    )}
                    {/* Botão Voltar à Fila - Backoffice pode usar quando a venda está atribuída a ele */}
                    {user?.role === 'backoffice' && selectedVenda.tratado_por === user.id && (
                      <div className="info-item" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                        <button
                          onClick={() => handleVoltarFila(selectedVenda)}
                          className="btn-action"
                          style={{ 
                            background: '#ff9800', 
                            color: 'white',
                            padding: '0.5rem 1rem',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          🔄 Voltar à Fila de Tratamento
                        </button>
                      </div>
                    )}
                    {/* Seção Aguardando Aceite - mostrar simulação e link para o vendedor */}
                    {selectedVenda.status === 'aguardando_aceite' && (
                      <div className="info-section" style={{ marginTop: '1.5rem', border: '2px solid var(--azul-dvs)', borderRadius: '8px', padding: '1rem', background: 'rgba(6, 102, 255, 0.05)' }}>
                        <h4 style={{ color: 'var(--azul-dvs)', marginBottom: '1rem' }}>📋 Aguardando Aceite</h4>
                        
                        {selectedVenda.simulacao && (
                          <div className="info-item" style={{ marginBottom: '1rem' }}>
                            <div className="info-label" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Simulação</div>
                            <div className="info-value" style={{ 
                              whiteSpace: 'pre-wrap', 
                              background: 'var(--cinza-escuro-2)', 
                              padding: '0.75rem', 
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)'
                            }}>
                              {selectedVenda.simulacao}
                            </div>
                          </div>
                        )}
                        
                        {selectedVenda.link_aceite && (
                          <div className="info-item" style={{ marginBottom: '1rem' }}>
                            <div className="info-label" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Link de Aceite</div>
                            <div className="info-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                              <a 
                                href={selectedVenda.link_aceite} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                  color: 'var(--azul-dvs)', 
                                  wordBreak: 'break-all',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  padding: '0.5rem 1rem',
                                  background: 'var(--cinza-escuro-2)',
                                  borderRadius: '6px',
                                  textDecoration: 'none'
                                }}
                              >
                                🔗 {selectedVenda.link_aceite}
                              </a>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(selectedVenda.link_aceite);
                                  alert('Link copiado para a área de transferência!');
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  borderRadius: '6px',
                                  border: 'none',
                                  background: 'var(--azul-dvs)',
                                  color: 'white',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                                title="Copiar link"
                              >
                                📋 Copiar
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Verificar Aceite e Alterar email - vendedor, supervisor, admin e diretor */}
                        {(user?.role === 'vendedor' || user?.role === 'admin' || user?.role === 'diretor' || user?.role === 'supervisor') && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '1rem', marginTop: '0.5rem' }}>
                            <VerificarAceiteButton 
                              venda={selectedVenda} 
                              user={user}
                              onVerificar={handleVerificarAceite}
                              verificandoAceite={verificandoAceite}
                            />
                            <BotoesAlterarDadosAceite
                              venda={selectedVenda}
                              user={user}
                              onAlterarEmail={openAlterarEmailAceiteModal}
                              onAlterarCelular={openAlterarCelularAceiteModal}
                              salvandoEmail={salvandoEmailAceite}
                              salvandoCelular={salvandoCelularAceite}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Seção Pendente Biometria - mostrar número do pedido PAP e botão Verificar Biometria */}
                    {selectedVenda.status === 'pendente_biometria' && (
                      <div className="info-section" style={{ marginTop: '1.5rem', border: '2px solid var(--azul-dvs)', borderRadius: '8px', padding: '1rem', background: 'rgba(6, 102, 255, 0.05)' }}>
                        <h4 style={{ color: 'var(--azul-dvs)', marginBottom: '1rem' }}>📋 Pendente Biometria</h4>

                        {selectedVenda.numero_pedido_pap && (
                          <div className="info-item" style={{ marginBottom: '1rem' }}>
                            <div className="info-label" style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Número do Pedido PAP</div>
                            <div className="info-value" style={{
                              whiteSpace: 'pre-wrap',
                              background: 'var(--cinza-escuro-2)',
                              padding: '0.75rem',
                              borderRadius: '6px',
                              border: '1px solid var(--border-color)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}>
                              <span style={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: '600' }}>
                                {selectedVenda.numero_pedido_pap}
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(selectedVenda.numero_pedido_pap);
                                  alert('Número do pedido PAP copiado!');
                                }}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  borderRadius: '4px',
                                  border: 'none',
                                  background: 'var(--azul-dvs)',
                                  color: 'white',
                                  cursor: 'pointer'
                                }}
                                title="Copiar número do pedido PAP"
                              >
                                📋 Copiar
                              </button>
                            </div>
                          </div>
                        )}

                        {selectedVenda.numero_pedido_pap && (
                          <div className="info-item" style={{ marginTop: '1rem' }}>
                            <button
                              onClick={() => setReenviandoBiometria(selectedVenda)}
                              className="btn-action"
                              style={{
                                background: '#ff9800',
                                color: 'white',
                                padding: '0.75rem 1.5rem',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                width: '100%'
                              }}
                            >
                              🔄 Reenviar Biometria
                            </button>
                          </div>
                        )}

                        {(user?.role === 'vendedor' || user?.role === 'admin' || user?.role === 'diretor' || user?.role === 'supervisor') && (
                          <VerificarBiometriaButton
                            venda={selectedVenda}
                            user={user}
                            onVerificar={handleVerificarBiometria}
                            verificandoBiometria={verificandoBiometria}
                          />
                        )}
                      </div>
                    )}

                    {(user?.role === 'admin' || user?.role === 'diretor') && (
                      <>
                        <div className="info-item" style={{ gridColumn: '1 / -1', marginTop: '1rem' }}>
                          <button
                            onClick={() => openAtribuicaoBko(selectedVenda)}
                            className="btn-action"
                            style={{ 
                              background: 'var(--azul-dvs)', 
                              color: 'white',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              marginRight: '0.5rem'
                            }}
                          >
                            🔄 {selectedVenda.tratado_por ? 'Trocar/Remover Atribuição de BKO' : 'Atribuir BKO'}
                          </button>
                          <button
                            onClick={() => openAtribuicaoVendedor(selectedVenda)}
                            className="btn-action"
                            style={{ 
                              background: '#4caf50', 
                              color: 'white',
                              padding: '0.5rem 1rem',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            👤 Trocar Vendedor
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Informações de Viabilidade */}
                {selectedVenda.operadora_selecionada && (
                  <div className="info-section">
                    <h4>📡 Informações de Viabilidade</h4>
                    <div className="info-grid">
                      <div className="info-item">
                        <div className="info-label">Operadora Selecionada</div>
                        <div className="info-value" style={{ fontWeight: '600' }}>
                          {selectedVenda.operadora_selecionada.charAt(0).toUpperCase() + selectedVenda.operadora_selecionada.slice(1)}
                        </div>
                      </div>
                      {selectedVenda.operadora_viabilidade_data && (
                        <div className="info-item">
                          <div className="info-label">Data da Consulta</div>
                          <div className="info-value">
                            {formatDateTime(selectedVenda.operadora_viabilidade_data)}
                          </div>
                        </div>
                      )}
                      {selectedVenda.operadora_viabilidade_json && (
                        <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                          <div className="info-label">Status de Viabilidade</div>
                          <div className="info-value">
                            {(() => {
                              try {
                                let viabilidade = selectedVenda.operadora_viabilidade_json;
                                
                                // Se for string, fazer parse
                                if (typeof viabilidade === 'string') {
                                  try {
                                    viabilidade = JSON.parse(viabilidade);
                                    // Se o resultado ainda for string (double-encoded), fazer parse novamente
                                    if (typeof viabilidade === 'string') {
                                      viabilidade = JSON.parse(viabilidade);
                                    }
                                  } catch (parseError) {
                                    console.error('Erro ao fazer parse do JSON:', parseError);
                                    return <span style={{ color: 'var(--error)' }}>Erro ao processar JSON de viabilidade</span>;
                                  }
                                }
                                
                                // Log para debug
                                console.log('📋 Viabilidade JSON parseado:', viabilidade);
                                console.log('📋 Tipo:', typeof viabilidade);
                                console.log('📋 Operadora selecionada:', selectedVenda.operadora_selecionada);
                                
                                // Se viabilidade é null ou undefined
                                if (!viabilidade) {
                                  console.warn('Viabilidade é null ou undefined');
                                  return <span style={{ color: 'var(--texto-secundario)' }}>Dados de viabilidade não disponíveis</span>;
                                }
                                
                                // Verificar se tem operadoras diretamente ou dentro de uma propriedade
                                let operadoras = null;
                                if (viabilidade.operadoras && typeof viabilidade.operadoras === 'object') {
                                  operadoras = viabilidade.operadoras;
                                } else if (viabilidade.data && viabilidade.data.operadoras) {
                                  operadoras = viabilidade.data.operadoras;
                                } else if (typeof viabilidade === 'object' && !Array.isArray(viabilidade)) {
                                  // Verificar se o objeto inteiro tem estrutura de operadoras
                                  const keys = Object.keys(viabilidade);
                                  const hasOperadoraStructure = keys.some(key => {
                                    const val = viabilidade[key];
                                    return val && typeof val === 'object' && ('coverage' in val);
                                  });
                                  if (hasOperadoraStructure) {
                                    operadoras = viabilidade;
                                  }
                                }
                                
                                if (!operadoras || (typeof operadoras === 'object' && Object.keys(operadoras).length === 0)) {
                                  console.warn('Viabilidade sem operadoras válidas:', {
                                    viabilidade,
                                    operadoras,
                                    keys: viabilidade ? Object.keys(viabilidade) : []
                                  });
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                      <span style={{ color: 'var(--texto-secundario)' }}>Dados de viabilidade não contêm operadoras</span>
                                      <details style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>
                                        <summary style={{ cursor: 'pointer' }}>Ver estrutura do JSON</summary>
                                        <pre style={{ 
                                          marginTop: '0.5rem', 
                                          padding: '0.5rem', 
                                          background: 'var(--cinza-escuro-2)', 
                                          borderRadius: '4px',
                                          overflow: 'auto',
                                          maxHeight: '200px'
                                        }}>
                                          {JSON.stringify(viabilidade, null, 2)}
                                        </pre>
                                      </details>
                                    </div>
                                  );
                                }

                                // Normalizar nome da operadora (case-insensitive, remover espaços extras)
                                const operadoraSelecionadaNormalizada = selectedVenda.operadora_selecionada 
                                  ? selectedVenda.operadora_selecionada.toLowerCase().trim()
                                  : null;

                                // Procurar operadora no JSON (case-insensitive)
                                let operadora = null;
                                if (operadoraSelecionadaNormalizada && operadoras) {
                                  // Tentar encontrar pelo nome exato primeiro
                                  operadora = operadoras[selectedVenda.operadora_selecionada];
                                  
                                  // Se não encontrar, procurar case-insensitive
                                  if (!operadora) {
                                    const operadorasKeys = Object.keys(operadoras);
                                    const operadoraEncontrada = operadorasKeys.find(key => 
                                      key.toLowerCase().trim() === operadoraSelecionadaNormalizada
                                    );
                                    if (operadoraEncontrada) {
                                      operadora = operadoras[operadoraEncontrada];
                                    }
                                  }
                                }

                                if (operadora) {
                                  // Normalizar coverage para boolean
                                  const hasCoverage = operadora.coverage === true || 
                                                     operadora.coverage === 'true' || 
                                                     operadora.coverage === 1 ||
                                                     operadora.coverage === '1';
                                  
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                      <span className={`status-badge ${hasCoverage ? 'status-concluido' : 'status-cancelado'}`} style={{
                                        background: hasCoverage ? '#d4edda' : '#f8d7da',
                                        color: '#000 !important',
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        display: 'inline-block',
                                        fontWeight: '600',
                                        width: 'fit-content'
                                      }}>
                                        {hasCoverage ? '✓ Viabilidade Confirmada' : '⚠ Sem Viabilidade Confirmada'}
                                      </span>
                                      
                                      {/* Mostrar todas as operadoras disponíveis */}
                                      {Object.keys(operadoras).length > 1 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                          <span style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', fontWeight: '600' }}>
                                            Todas as operadoras consultadas:
                                          </span>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                            {Object.keys(operadoras).map(nome => {
                                              const op = operadoras[nome];
                                              const opHasCoverage = op && (op.coverage === true || op.coverage === 'true' || op.coverage === 1 || op.coverage === '1');
                                              const isSelected = nome.toLowerCase() === operadoraSelecionadaNormalizada;
                                              return (
                                                <span 
                                                  key={nome} 
                                                  style={{
                                                    padding: '0.4rem 0.75rem',
                                                    background: opHasCoverage ? '#d4edda' : '#f8d7da',
                                                    color: opHasCoverage ? '#155724' : '#721c24',
                                                    borderRadius: '6px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: isSelected ? '600' : '400',
                                                    border: isSelected ? '2px solid var(--azul-dvs)' : '1px solid transparent'
                                                  }}
                                                >
                                                  {nome.charAt(0).toUpperCase() + nome.slice(1)}: {opHasCoverage ? '✓' : '✗'}
                                                  {isSelected && ' (Selecionada)'}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                                
                                // Se não encontrou a operadora específica, mostrar todas as operadoras disponíveis
                                const operadorasDisponiveis = Object.keys(operadoras || {});
                                if (operadorasDisponiveis.length > 0) {
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                      {selectedVenda.operadora_selecionada && (
                                        <span style={{ color: 'var(--texto-secundario)', fontSize: '0.9rem' }}>
                                          Operadora "{selectedVenda.operadora_selecionada}" não encontrada no JSON.
                                        </span>
                                      )}
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        {operadorasDisponiveis.map(nome => {
                                          const op = operadoras[nome];
                                          const hasCoverage = op && (op.coverage === true || op.coverage === 'true' || op.coverage === 1 || op.coverage === '1');
                                          return (
                                            <span key={nome} style={{
                                              padding: '0.25rem 0.5rem',
                                              background: hasCoverage ? '#d4edda' : '#f8d7da',
                                              color: hasCoverage ? '#155724' : '#721c24',
                                              borderRadius: '4px',
                                              fontSize: '0.85rem'
                                            }}>
                                              {nome}: {hasCoverage ? '✓' : '✗'}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                }
                                
                                return <span style={{ color: 'var(--texto-secundario)' }}>Nenhuma operadora encontrada no JSON</span>;
                              } catch (e) {
                                console.error('Erro ao processar viabilidade:', e);
                                return <span style={{ color: 'var(--error)' }}>Erro ao processar dados de viabilidade</span>;
                              }
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Envio de Aceite */}
                <div className="info-section" style={{ marginTop: '1.5rem' }}>
                  <h4>📧 Envio de Aceite</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <div className="info-label">Operadora</div>
                      <div className="info-value">
                        {selectedVenda.envio_aceite ? (() => {
                          const parts = selectedVenda.envio_aceite.split(':');
                          return parts.length > 0 
                            ? parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
                            : '-';
                        })() : 'Não informado'}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Meios de Envio</div>
                      <div className="info-value">
                        {selectedVenda.envio_aceite ? (() => {
                          const parts = selectedVenda.envio_aceite.split(':');
                          if (parts.length < 2) return '-';
                          
                          const meios = parts[1].split(',');
                          return <MeiosEnvioList meios={meios} />;
                        })() : 'Não informado'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Editar Venda */}
        {editingVenda && (
          <EditarVendaModal
            venda={editingVenda}
            user={user}
            statusOptions={STATUS_OPTIONS}
            onClose={closeModal}
            onSaved={() => {
              closeModal();
              loadVendas();
            }}
          />
        )}

        {/* Modal de Mudar Status */}
        {changingStatus && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>🔄 Alterar Status - Venda #{changingStatus.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <div className="modal-body-venda">
                <div className="info-section">
                  <div className="info-item">
                    <div className="info-label">Status Atual</div>
                    <div className="info-value">
                      <span className={`status-badge ${getStatusClass(changingStatus.status)}`}>
                        {getStatusText(changingStatus.status)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="form-group" style={{ marginTop: '1.5rem' }}>
                    <label>Novo Status *</label>
                    <select
                      value={newStatus}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewStatus(val);
                        if (val === 'aguardando_instalacao' || val === 'validacao_ok') {
                          setNewDataInstalacao(changingStatus?.data_instalacao || changingStatus?.data_agendamento1 || getTodayBrasilia());
                          setNewNumeroOrdem(changingStatus?.numero_ordem || '');
                        } else {
                          setNewDataInstalacao('');
                          setNewNumeroOrdem('');
                        }
                        if (val === 'concluido') {
                          setNewDataConclusao(changingStatus?.data_conclusao || getTodayBrasilia());
                        } else {
                          setNewDataConclusao('');
                        }
                      }}
                      style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                    >
                      <option value="">Selecione o status</option>
                      {STATUS_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {(newStatus === 'aguardando_instalacao' || newStatus === 'validacao_ok') && (
                    <>
                      <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label>Data da Instalação *</label>
                        <input
                          type="date"
                          value={newDataInstalacao || ''}
                          onChange={(e) => setNewDataInstalacao(e.target.value)}
                          style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                        />
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                          {newStatus === 'validacao_ok'
                            ? 'Obrigatório para Validação OK.'
                            : 'Data em que está agendada a instalação (usada no filtro da página Vendas).'}
                        </p>
                      </div>
                      <div className="form-group" style={{ marginTop: '1rem' }}>
                        <label>Número da Ordem{newStatus === 'validacao_ok' ? ' *' : ''}</label>
                        <input
                          type="text"
                          value={newNumeroOrdem || ''}
                          onChange={(e) => setNewNumeroOrdem(e.target.value)}
                          placeholder="Ex: 123456"
                          style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                        />
                        <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                          {newStatus === 'validacao_ok'
                            ? 'Obrigatório para Validação OK.'
                            : 'Número da ordem de instalação (opcional).'}
                        </p>
                      </div>
                    </>
                  )}

                  {newStatus === 'concluido' && (
                    <div className="form-group" style={{ marginTop: '1rem' }}>
                      <label>Data de Conclusão *</label>
                      <input
                        type="date"
                        value={newDataConclusao || ''}
                        onChange={(e) => setNewDataConclusao(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      />
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.25rem' }}>
                        Data em que a venda foi concluída.
                      </p>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={closeModal} className="btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleChangeStatus}
                    className="btn-primary"
                    disabled={
                      !newStatus
                      || ((newStatus === 'aguardando_instalacao' || newStatus === 'validacao_ok') && !newDataInstalacao?.trim())
                      || (newStatus === 'validacao_ok' && !newNumeroOrdem?.trim())
                      || (newStatus === 'concluido' && !newDataConclusao?.trim())
                    }
                  >
                    ✅ Alterar Status
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Atribuição de BKO */}
        {changingAtribuicao && (user?.role === 'admin' || user?.role === 'diretor') && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>🔄 Atribuir/Trocar BKO - Venda #{changingAtribuicao.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <div className="modal-body-venda">
                <form onSubmit={handleAtribuicaoBkoSubmit}>
                  <div className="info-section">
                    <div className="info-item">
                      <div className="info-label">BKO Atual</div>
                      <div className="info-value">
                        {changingAtribuicao.tratador?.nome || 'Não atribuído'}
                      </div>
                    </div>
                    
                    <div className="form-group" style={{ marginTop: '1.5rem' }}>
                      <label>Atribuir para BKO:</label>
                      <select
                        value={novoTratadoPor}
                        onChange={(e) => setNovoTratadoPor(e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          background: 'var(--cinza-escuro-2)', 
                          border: '1px solid var(--border-color)', 
                          borderRadius: '8px', 
                          color: 'var(--texto-primario)' 
                        }}
                      >
                        <option value="">Limpar atribuição (Não atribuído)</option>
                        {backoffices.map(bko => (
                          <option key={bko.id} value={bko.id}>
                            {bko.nome} ({bko.username})
                          </option>
                        ))}
                      </select>
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
                        Selecione um BKO para atribuir ou deixe vazio para remover a atribuição.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                    <button type="button" onClick={closeModal} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary">
                      ✅ {(!novoTratadoPor || novoTratadoPor === '' || novoTratadoPor.toString().trim() === '') 
                        ? 'Remover Atribuição' 
                        : 'Atualizar Atribuição'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Troca de Vendedor */}
        {changingVendedor && (user?.role === 'admin' || user?.role === 'diretor') && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>👤 Trocar Vendedor - Venda #{changingVendedor.id}</h2>
                <span className="close-modal" onClick={closeModal}>×</span>
              </div>
              
              <div className="modal-body-venda">
                <form onSubmit={handleAtribuicaoVendedorSubmit}>
                  <div className="info-section">
                    <div className="info-item">
                      <div className="info-label">Vendedor Atual</div>
                      <div className="info-value">
                        {changingVendedor.vendedor?.nome || 'Não atribuído'}
                      </div>
                    </div>
                    
                    <div className="form-group" style={{ marginTop: '1.5rem' }}>
                      <label>Selecione o novo vendedor: *</label>
                      <select
                        value={novoVendedorId}
                        onChange={(e) => setNovoVendedorId(e.target.value)}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          background: 'var(--cinza-escuro-2)', 
                          border: '1px solid var(--border-color)', 
                          borderRadius: '8px', 
                          color: 'var(--texto-primario)' 
                        }}
                        required
                      >
                        <option value="">Selecione um vendedor</option>
                        {vendedores.map(vendedor => (
                          <option key={vendedor.id} value={vendedor.id}>
                            {vendedor.nome} ({vendedor.username}) - {vendedor.role}
                          </option>
                        ))}
                      </select>
                      <p style={{ fontSize: '0.85rem', color: 'var(--texto-secundario)', marginTop: '0.5rem' }}>
                        Selecione o vendedor responsável por esta venda.
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                    <button type="button" onClick={closeModal} className="btn-secondary">
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={!novoVendedorId}>
                      ✅ Atualizar Vendedor
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Alterar celular (Aguardando Aceite) */}
        {alterarCelularVenda && (
          <div
            className="modal-overlay"
            onClick={() => {
              if (!salvandoCelularAceite) {
                setAlterarCelularVenda(null);
                setNovoCelularAlterarAceite('');
              }
            }}
          >
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>📱 Alterar celular - Venda #{alterarCelularVenda.id}</h2>
                <span
                  className="close-modal"
                  onClick={() => {
                    if (!salvandoCelularAceite) {
                      setAlterarCelularVenda(null);
                      setNovoCelularAlterarAceite('');
                    }
                  }}
                >
                  ×
                </span>
              </div>
              <div className="modal-body-venda">
                <form onSubmit={handleSalvarAlterarCelularAceite}>
                  <div className="info-section">
                    <div className="info-item" style={{ marginBottom: '1rem' }}>
                      <div className="info-label">Celular atual</div>
                      <div className="info-value">{formatPhone(alterarCelularVenda.telefone) || '—'}</div>
                    </div>
                    <div className="info-item" style={{ marginBottom: '1rem' }}>
                      <label htmlFor="novo-celular-aceite" className="info-label">Novo celular</label>
                      <input
                        id="novo-celular-aceite"
                        type="tel"
                        autoComplete="tel"
                        value={novoCelularAlterarAceite}
                        onChange={(e) => setNovoCelularAlterarAceite(maskPhone(e.target.value))}
                        style={{
                          width: '100%',
                          marginTop: '0.35rem',
                          padding: '0.6rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--cinza-escuro-2)',
                          color: 'var(--texto-primario)'
                        }}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div
                      style={{
                        background: 'rgba(92, 107, 192, 0.12)',
                        border: '1px solid rgba(92, 107, 192, 0.35)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <p style={{ margin: 0, color: 'var(--texto-primario)', fontSize: '0.9rem' }}>
                        <strong>O que acontece:</strong> o celular do cliente é atualizado, a venda volta para{' '}
                        <strong>Pendente BKO</strong> e o histórico de tratamento registra a alteração com o novo celular.
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setAlterarCelularVenda(null);
                        setNovoCelularAlterarAceite('');
                      }}
                      className="btn-secondary"
                      disabled={salvandoCelularAceite}
                    >
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={salvandoCelularAceite || !novoCelularAlterarAceite.replace(/\D/g, '')}>
                      {salvandoCelularAceite ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal Alterar email (Aguardando Aceite) */}
        {alterarEmailVenda && (
          <div
            className="modal-overlay"
            onClick={() => {
              if (!salvandoEmailAceite) {
                setAlterarEmailVenda(null);
                setNovoEmailAlterarAceite('');
              }
            }}
          >
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>✉️ Alterar email - Venda #{alterarEmailVenda.id}</h2>
                <span
                  className="close-modal"
                  onClick={() => {
                    if (!salvandoEmailAceite) {
                      setAlterarEmailVenda(null);
                      setNovoEmailAlterarAceite('');
                    }
                  }}
                >
                  ×
                </span>
              </div>
              <div className="modal-body-venda">
                <form onSubmit={handleSalvarAlterarEmailAceite}>
                  <div className="info-section">
                    <div className="info-item" style={{ marginBottom: '1rem' }}>
                      <div className="info-label">Email atual</div>
                      <div className="info-value">{alterarEmailVenda.email || '—'}</div>
                    </div>
                    <div className="info-item" style={{ marginBottom: '1rem' }}>
                      <label htmlFor="novo-email-aceite" className="info-label">Novo email</label>
                      <input
                        id="novo-email-aceite"
                        type="email"
                        autoComplete="email"
                        value={novoEmailAlterarAceite}
                        onChange={(e) => setNovoEmailAlterarAceite(e.target.value)}
                        style={{
                          width: '100%',
                          marginTop: '0.35rem',
                          padding: '0.6rem 0.75rem',
                          borderRadius: '6px',
                          border: '1px solid var(--border-color)',
                          background: 'var(--cinza-escuro-2)',
                          color: 'var(--texto-primario)'
                        }}
                        placeholder="cliente@exemplo.com"
                      />
                    </div>
                    <div
                      style={{
                        background: 'rgba(92, 107, 192, 0.12)',
                        border: '1px solid rgba(92, 107, 192, 0.35)',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}
                    >
                      <p style={{ margin: 0, color: 'var(--texto-primario)', fontSize: '0.9rem' }}>
                        <strong>O que acontece:</strong> o email do cliente é atualizado, a venda volta para{' '}
                        <strong>Pendente BKO</strong> e o histórico de tratamento registra a alteração com o novo email.
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setAlterarEmailVenda(null);
                        setNovoEmailAlterarAceite('');
                      }}
                      className="btn-secondary"
                      disabled={salvandoEmailAceite}
                    >
                      Cancelar
                    </button>
                    <button type="submit" className="btn-primary" disabled={salvandoEmailAceite || !novoEmailAlterarAceite.trim()}>
                      {salvandoEmailAceite ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Reenvio de Biometria */}
        {reenviandoBiometria && (
          <div className="modal-overlay" onClick={() => { setReenviandoBiometria(null); setMetodoReenvio(''); }}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header-venda">
                <h2>🔄 Reenviar Biometria - Venda #{reenviandoBiometria.id}</h2>
                <span className="close-modal" onClick={() => { setReenviandoBiometria(null); setMetodoReenvio(''); }}>×</span>
              </div>
              
              <div className="modal-body-venda">
                <div className="info-section">
                  {/* Dados do Cliente */}
                  <h4 style={{ marginBottom: '1rem' }}>Dados do Cliente</h4>
                  <div className="info-grid" style={{ marginBottom: '1.5rem' }}>
                    <div className="info-item">
                      <div className="info-label">Cliente</div>
                      <div className="info-value">
                        {reenviandoBiometria.nome_cliente || reenviandoBiometria.razao_social || '-'}
                      </div>
                    </div>
                    <div className="info-item">
                      <div className="info-label">Telefone 1</div>
                      <div className="info-value">
                        {reenviandoBiometria.telefone || '-'}
                      </div>
                    </div>
                    {reenviandoBiometria.telefone2 && (
                      <div className="info-item">
                        <div className="info-label">Telefone 2</div>
                        <div className="info-value">
                          {reenviandoBiometria.telefone2}
                        </div>
                      </div>
                    )}
                    <div className="info-item">
                      <div className="info-label">Email</div>
                      <div className="info-value">
                        {reenviandoBiometria.email || '-'}
                      </div>
                    </div>
                  </div>

                  {/* Seleção do Método */}
                  <h4 style={{ marginBottom: '1rem' }}>Selecione o Método de Reenvio</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '1rem',
                      background: metodoReenvio === 'sms' ? 'var(--azul-dvs)' : 'var(--cinza-escuro-2)',
                      border: `2px solid ${metodoReenvio === 'sms' ? 'var(--azul-dvs)' : 'var(--border-color)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: metodoReenvio === 'sms' ? 'white' : 'var(--texto-primario)',
                      flex: '1 1 120px',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="radio"
                        name="metodoReenvio"
                        value="sms"
                        checked={metodoReenvio === 'sms'}
                        onChange={(e) => setMetodoReenvio(e.target.value)}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: '1.5rem' }}>📱</span>
                      <span style={{ fontWeight: '600' }}>SMS</span>
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '1rem',
                      background: metodoReenvio === 'email' ? 'var(--azul-dvs)' : 'var(--cinza-escuro-2)',
                      border: `2px solid ${metodoReenvio === 'email' ? 'var(--azul-dvs)' : 'var(--border-color)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: metodoReenvio === 'email' ? 'white' : 'var(--texto-primario)',
                      flex: '1 1 120px',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="radio"
                        name="metodoReenvio"
                        value="email"
                        checked={metodoReenvio === 'email'}
                        onChange={(e) => setMetodoReenvio(e.target.value)}
                        style={{ display: 'none' }}
                      />
                      <span style={{ fontSize: '1.5rem' }}>📧</span>
                      <span style={{ fontWeight: '600' }}>Email</span>
                    </label>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      padding: '1rem',
                      background: metodoReenvio === 'whatsapp' ? 'var(--azul-dvs)' : 'var(--cinza-escuro-2)',
                      border: `2px solid ${metodoReenvio === 'whatsapp' ? 'var(--azul-dvs)' : 'var(--border-color)'}`,
                      borderRadius: '8px',
                      cursor: 'pointer',
                      color: metodoReenvio === 'whatsapp' ? 'white' : 'var(--texto-primario)',
                      flex: '1 1 120px',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="radio"
                        name="metodoReenvio"
                        value="whatsapp"
                        checked={metodoReenvio === 'whatsapp'}
                        onChange={(e) => setMetodoReenvio(e.target.value)}
                        style={{ display: 'none' }}
                      />
                      <WhatsAppIcon
                        size={26}
                        color={metodoReenvio === 'whatsapp' ? '#fff' : '#25D366'}
                      />
                      <span style={{ fontWeight: '600' }}>WhatsApp</span>
                    </label>
                  </div>

                  {/* Informação sobre o que acontece */}
                  <div style={{ 
                    background: 'rgba(255, 152, 0, 0.1)', 
                    border: '1px solid rgba(255, 152, 0, 0.3)', 
                    borderRadius: '8px', 
                    padding: '1rem',
                    marginBottom: '1rem' 
                  }}>
                    <p style={{ margin: 0, color: 'var(--texto-primario)', fontSize: '0.9rem' }}>
                      <strong>ℹ️ O que acontece:</strong><br />
                      A venda voltará para <strong>Pendente BKO</strong> com uma observação registrada para o BKO realizar o reenvio.
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={() => { setReenviandoBiometria(null); setMetodoReenvio(''); }} className="btn-secondary">
                    Cancelar
                  </button>
                  <button
                    onClick={handleReenviarBiometria}
                    className="btn-primary"
                    disabled={!metodoReenvio}
                    style={{ opacity: !metodoReenvio ? 0.6 : 1 }}
                  >
                    ✅ Confirmar Reenvio
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showProcessarVendas && (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !processarLoading && resetProcessarVendas()}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
              <div className="modal-header">
                <h2>Processar Vendas</h2>
                <button type="button" className="modal-close" onClick={resetProcessarVendas} disabled={processarLoading}>✕</button>
              </div>
              <div className="modal-body">
                {processarStep === 'tipo' && (
                  <>
                    <p style={{ marginBottom: '1rem', color: 'var(--texto-secundario)' }}>
                      Selecione o tipo de documento da planilha:
                    </p>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ flex: 1, padding: '1rem' }}
                        onClick={() => { setProcessarTipo('cpf'); setProcessarStep('acao'); }}
                      >
                        CPF
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ flex: 1, padding: '1rem' }}
                        onClick={() => { setProcessarTipo('cnpj'); setProcessarStep('acao'); }}
                      >
                        CNPJ
                      </button>
                    </div>
                  </>
                )}
                {processarStep === 'acao' && (
                  <>
                    <p style={{ marginBottom: '0.5rem' }}>
                      Tipo selecionado: <strong>{processarTipo.toUpperCase()}</strong>
                    </p>
                    <p style={{ marginBottom: '1rem', color: 'var(--texto-secundario)' }}>
                      Selecione a ação a aplicar nas vendas com status Aguardando Instalação:
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <button
                        type="button"
                        className="btn btn-success"
                        style={{ padding: '0.85rem' }}
                        onClick={() => { setProcessarAcao('concluido'); setProcessarStep('upload'); }}
                      >
                        Concluído
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '0.85rem' }}
                        onClick={() => { setProcessarAcao('instalacao_cancelada'); setProcessarStep('upload'); }}
                      >
                        Instalação Cancelada
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ marginTop: '1rem' }}
                      onClick={() => setProcessarStep('tipo')}
                    >
                      ← Voltar
                    </button>
                  </>
                )}
                {processarStep === 'upload' && (
                  <>
                    <p style={{ marginBottom: '0.5rem' }}>
                      <strong>{processarTipo.toUpperCase()}</strong> — {processarAcao === 'concluido' ? 'Concluído' : 'Instalação Cancelada'}
                    </p>
                    <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                      {getProcessarPlanilhaDesc()}
                    </p>
                    {processarAcao === 'instalacao_cancelada' && (
                      <p style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--texto-secundario)' }}>
                        A data da coluna B será ignorada. A baixa será registrada na data de hoje.
                      </p>
                    )}
                    <input
                      ref={processarFileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleProcessarVendasFile}
                      disabled={processarLoading}
                      style={{ marginBottom: '1rem', width: '100%' }}
                    />
                    {processarLoading && (
                      <p style={{ color: 'var(--azul-dvs)' }}>Processando planilha...</p>
                    )}
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setProcessarStep('acao')}
                      disabled={processarLoading}
                    >
                      ← Voltar
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {showProcessarPlanilha && (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !processarPlanilhaLoading && resetProcessarPlanilha()}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
              <div className="modal-header">
                <h2>Processar Planilha</h2>
                <button type="button" className="modal-close" onClick={resetProcessarPlanilha} disabled={processarPlanilhaLoading}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: '0.75rem', color: 'var(--texto-secundario)' }}>
                  Envie a planilha XLSX (formato ATT ALLIANCE) para baixar vendas em Aguardando Instalação:
                </p>
                <ul style={{ marginBottom: '1rem', paddingLeft: '1.25rem', fontSize: '0.9rem', color: 'var(--texto-secundario)' }}>
                  <li><strong>Coluna A:</strong> Número da ordem</li>
                  <li><strong>Coluna B:</strong> Status — INSTALADO → Concluído | CANCELADO → Instalação cancelada</li>
                  <li><strong>Coluna C:</strong> Data (obrigatória para INSTALADO; usa só a data, ignora horário)</li>
                </ul>
                <input
                  ref={processarPlanilhaFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleProcessarPlanilhaFile}
                  disabled={processarPlanilhaLoading}
                  style={{ marginBottom: '1rem', width: '100%' }}
                />
                {processarPlanilhaLoading && (
                  <p style={{ color: 'var(--azul-dvs)' }}>Processando planilha...</p>
                )}
              </div>
            </div>
          </div>
        )}
        {showExportModal && (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeExportModal()}>
            <div className="modal-content-venda" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
              <div className="modal-header-venda">
                <h2>📥 Exportar Vendas</h2>
                <span className="close-modal" onClick={closeExportModal}>×</span>
              </div>
              <div className="modal-body-venda">
                <p style={{ marginBottom: '1rem', color: 'var(--texto-secundario)', fontSize: '0.9rem' }}>
                  Escolha o período da exportação. Os demais filtros da página (status, vendedor, operadora, etc.) serão mantidos.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="export_periodo_tipo"
                      checked={exportPeriodoTipo === 'mes'}
                      onChange={() => setExportPeriodoTipo('mes')}
                    />
                    <span>Por mês</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="export_periodo_tipo"
                      checked={exportPeriodoTipo === 'intervalo'}
                      onChange={() => setExportPeriodoTipo('intervalo')}
                    />
                    <span>Por intervalo de datas</span>
                  </label>
                </div>

                {exportPeriodoTipo === 'mes' ? (
                  <div className="form-group">
                    <label>Mês de referência *</label>
                    <select
                      value={`${exportMes}|${exportAno}`}
                      onChange={(e) => {
                        const [m, a] = e.target.value.split('|').map(Number);
                        setExportMes(m);
                        setExportAno(a);
                      }}
                      style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                    >
                      {opcoesMesesVendas.filter((opt) => opt.value).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label>Data início *</label>
                      <input
                        type="text"
                        placeholder="dd/mm/aaaa"
                        value={exportDataInicioDisplay}
                        onChange={(e) => handleExportDataChange('inicio', e.target.value)}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                    <div className="form-group">
                      <label>Data fim *</label>
                      <input
                        type="text"
                        placeholder="dd/mm/aaaa"
                        value={exportDataFimDisplay}
                        onChange={(e) => handleExportDataChange('fim', e.target.value)}
                        style={{ width: '100%', padding: '0.75rem', background: 'var(--cinza-escuro-2)', border: '1px solid var(--border-color)', borderRadius: '8px', color: 'var(--texto-primario)' }}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                  <button type="button" onClick={closeExportModal} className="btn-secondary" disabled={exportLoading}>
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleExportVendas}
                    className="btn btn-success"
                    disabled={exportLoading}
                  >
                    {exportLoading ? '⏳ Exportando...' : '📥 Exportar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {processarResultado && (
          <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeProcessarRelatorio()}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
              <div className="modal-header">
                <h2>
                  Relatório — {processarResultado.origem === 'planilha' ? 'Processar Planilha' : 'Processar Vendas'}
                </h2>
                <button type="button" className="modal-close" onClick={closeProcessarRelatorio}>✕</button>
              </div>
              <div className="modal-body">
                <p style={{ marginBottom: '1rem' }}>
                  <strong>{processarResultado.processados?.length || 0}</strong> processada(s),{' '}
                  <strong>{processarResultado.nao_processados?.length || 0}</strong> não processada(s)
                  {' '}(total de {processarResultado.total_linhas || 0} linha(s) na planilha)
                </p>
                {(processarResultado.processados?.length > 0) && (
                  <>
                    <h3 style={{ marginBottom: '0.5rem', color: 'var(--azul-dvs)' }}>Processadas</h3>
                    <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                      <table className="vendas-table" style={{ fontSize: '0.85rem', minWidth: '100%' }}>
                        <thead>
                          <tr>
                            <th>Linha</th>
                            <th>Venda</th>
                            {processarResultado.origem === 'planilha' ? (
                              <th>Número da Ordem</th>
                            ) : (
                              <>
                                <th>Documento</th>
                                <th>Identificador</th>
                              </>
                            )}
                            <th>Status</th>
                            <th>Data</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processarResultado.processados.map((item, i) => (
                            <tr key={`p-${i}`}>
                              <td>{item.linha}</td>
                              <td>#{item.sale_id}</td>
                              {processarResultado.origem === 'planilha' ? (
                                <td>{item.numero_ordem || '—'}</td>
                              ) : (
                                <>
                                  <td>{formatDocumento(item.documento)}</td>
                                  <td>{item.identificador || '—'}</td>
                                </>
                              )}
                              <td>{item.status_anterior} → {item.status_novo}</td>
                              <td>{item.data_aplicada ? formatDateOnly(item.data_aplicada) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {(processarResultado.nao_processados?.length > 0) && (
                  <>
                    <h3 style={{ marginBottom: '0.5rem', color: '#b91c1c' }}>Não processadas</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="vendas-table" style={{ fontSize: '0.85rem', minWidth: '100%' }}>
                        <thead>
                          <tr>
                            <th>Linha</th>
                            {processarResultado.origem === 'planilha' ? (
                              <th>Número da Ordem</th>
                            ) : (
                              <>
                                <th>Documento</th>
                                <th>Identificador</th>
                              </>
                            )}
                            <th>Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {processarResultado.nao_processados.map((item, i) => (
                            <tr key={`n-${i}`}>
                              <td>{item.linha}</td>
                              {processarResultado.origem === 'planilha' ? (
                                <td>{item.numero_ordem || '—'}</td>
                              ) : (
                                <>
                                  <td>{item.documento ? formatDocumento(item.documento) : '—'}</td>
                                  <td>{item.identificador || '—'}</td>
                                </>
                              )}
                              <td>{item.motivo}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem', borderTop: '1px solid var(--border-color)' }}>
                <button type="button" className="btn btn-primary" onClick={closeProcessarRelatorio}>
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Vendas;
